if (!customElements.get('feeding-calculator')) {
  customElements.define(
    'feeding-calculator',
    class FeedingCalculator extends HTMLElement {
      constructor() {
        super();
        this.slides = [];
        this.currentSlideIndex = 0;
        this.sectionId = this.dataset.sectionId;
        this.blockId = this.dataset.blockId;
        this.isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;
        
        // State management
        this.formData = {};
        this.navigationHistory = [];
        this.storageKey = `feeding-calculator-${this.sectionId}-${this.blockId || 'default'}`;
        
        // Initialize calculator module
        this.calculator = null;
        this.initializeCalculator();
        
        // Bind methods for event listeners
        this.boundHandleInputChange = this.handleInputChange.bind(this);
        this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      }

      initializeCalculator() {
        // Initialize calculator module
        if (typeof PetFeedingCalculator !== 'undefined' && !this.calculator) {
          this.calculator = new PetFeedingCalculator();
        }
      }

      connectedCallback() {
        this.initSlides();
        this.setupEventListeners();
        this.initProgressBar();
        this.setupResizeObserver();
        
        if (this.isDesignMode) {
          this.setupDesignMode();
        } else {
          // Check for product category from template
          const templateCategory = this.dataset.templateCategory;
          
          // Restore state from localStorage
          this.restoreFromLocalStorage();
          
          // If product category is cat or dog, auto-select pet and skip pet selection slide
          if (templateCategory === 'cat' || templateCategory === 'dog') {
            // Set pet_type based on category
            this.formData.pet_type = templateCategory;
            
            // Find the appropriate pet slide
            const targetSlide = this.slides.find(slide => {
              if (templateCategory === 'cat') {
                return slide.classList.contains('feeding-calculator-slide--cat') ||
                       slide.dataset.slideType === 'cat' ||
                       slide.dataset.slideIdentifier === 'cat-details';
              } else if (templateCategory === 'dog') {
                return slide.classList.contains('feeding-calculator-slide--dog') ||
                       slide.dataset.slideType === 'dog' ||
                       slide.dataset.slideIdentifier === 'dog-details';
              }
              return false;
            });
            
            if (targetSlide && this.slides.length > 0) {
              const slideIndex = parseInt(targetSlide.dataset.slideIndex, 10);
              this.ensureDefaultSelections(targetSlide);
              this.addToHistory(targetSlide, 'initial');
              targetSlide.classList.add('feeding-calculator-slide--active');
              this.currentSlideIndex = slideIndex;
              this.updateProgressBar();
              this.persistToLocalStorage();
            } else {
              // Fallback to first slide if pet slide not found
              const firstSlide = this.slides[0];
              this.ensureDefaultSelections(firstSlide);
              this.addToHistory(firstSlide, 'initial');
              firstSlide.classList.add('feeding-calculator-slide--active');
              this.currentSlideIndex = 0;
              this.updateProgressBar();
              this.persistToLocalStorage();
            }
          } else if (this.slides.length > 0 && this.navigationHistory.length === 0) {
            // Show first slide by default if no saved state and no category detected
            const firstSlide = this.slides[0];
            this.ensureDefaultSelections(firstSlide);
            this.addToHistory(firstSlide, 'initial');
            firstSlide.classList.add('feeding-calculator-slide--active');
            this.currentSlideIndex = 0;
            this.updateProgressBar();
            this.persistToLocalStorage();
          } else if (this.navigationHistory.length > 0) {
            // Restore to last visited slide
            const lastHistoryEntry = this.navigationHistory[this.navigationHistory.length - 1];
            const slide = this.findSlideById(lastHistoryEntry.slideId);
            if (slide) {
              const slideIndex = parseInt(slide.dataset.slideIndex, 10);
              this.showSlide(slideIndex, true); // Skip transition on restore
              this.restoreFormData();
            }
          }
        }
      }

      disconnectedCallback() {
        this.removeEventListeners();
        if (this.isDesignMode && this.boundHandleBlockSelect && this.boundHandleSectionLoad) {
          document.removeEventListener('shopify:block:select', this.boundHandleBlockSelect);
          document.removeEventListener('shopify:section:load', this.boundHandleSectionLoad);
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }

      setupResizeObserver() {
        // Watch for content size changes in active slides
        if (typeof ResizeObserver === 'undefined') return;
        
        this.resizeObserver = new ResizeObserver(() => {
          // Only update if there's an active slide
          const activeSlide = this.slides[this.currentSlideIndex];
          if (activeSlide && activeSlide.classList.contains('feeding-calculator-slide--active')) {
            this.updateWrapperHeight();
          }
        });
        
        // Observe all slides for size changes
        this.slides.forEach(slide => {
          this.resizeObserver.observe(slide);
        });
      }

      initSlides() {
        this.slides = Array.from(this.querySelectorAll('.feeding-calculator-slide'));
        
        if (this.slides.length === 0) return;

        // Set up resize observer for slides if it exists
        if (this.resizeObserver) {
          this.slides.forEach(slide => {
            this.resizeObserver.observe(slide);
          });
        }

        // Store slide identifiers/indexes for navigation
        this.slides.forEach((slide, index) => {
          const slideId = slide.id || `slide-${index}`;
          const blockId = slide.dataset.slideId || slide.id.replace('slide-', '');
          
          slide.dataset.slideIndex = index;
          slide.dataset.slideNumber = index;
          if (!slide.id) {
            slide.id = slideId;
          }
          
          // Store slide type for navigation logic
          if (slide.classList.contains('feeding-calculator-slide--cat')) {
            slide.dataset.slideType = 'cat';
          } else if (slide.classList.contains('feeding-calculator-slide--dog')) {
            slide.dataset.slideType = 'dog';
          } else if (slide.classList.contains('feeding-calculator-slide--buttons')) {
            slide.dataset.slideType = 'buttons';
          } else {
            slide.dataset.slideType = slide.dataset.slideType || 'unknown';
          }
        });
      }

      initProgressBar() {
        // Find progress bar elements in parent block
        const parentBlock = this.closest('.feeding-calculator-main');
        if (!parentBlock) return;
        
        this.progressBar = parentBlock.querySelector('[data-progress-bar]');
        this.progressBarFill = parentBlock.querySelector('[data-progress-bar-fill]');
        this.progressBarText = parentBlock.querySelector('[data-progress-bar-text]');
      }

      updateWrapperHeight(previousSlideHeight = null) {
        // Find the slides wrapper
        const slidesWrapper = this.querySelector('.feeding-calculator-main__slides-wrapper');
        if (!slidesWrapper) return;

        // Find the active slide
        const activeSlide = this.slides[this.currentSlideIndex];
        if (!activeSlide) return;

        // Use double requestAnimationFrame to ensure layout is complete after all transitions
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Check if slide is still active (might have changed during async operations)
            if (!activeSlide.classList.contains('feeding-calculator-slide--active')) {
              return;
            }

            // Force a reflow to ensure all content is measured
            void activeSlide.offsetHeight;
            
            // Measure the slide's height (it should be visible if active)
            const slideHeight = activeSlide.scrollHeight;
            
            // Always update wrapper height to match active slide (even if smaller)
            // This ensures wrapper shrinks when going back to shorter slides
            if (slideHeight > 0) {
              slidesWrapper.style.height = `${slideHeight}px`;
              
              // If navigating from a taller slide to a shorter one, scroll to top with -100px offset
              if (previousSlideHeight !== null && slideHeight < previousSlideHeight) {
                // Use requestAnimationFrame to ensure the height change has been applied
                requestAnimationFrame(() => {
                  // Scroll the calculator section to top with -100px offset
                  const calculatorElement = slidesWrapper.closest('.shopify-section');
                  if (calculatorElement) {
                    const elementTop = calculatorElement.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementTop - 100;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                  } else {
                    // Fallback: scroll the wrapper itself if calculator element not found
                    const wrapperTop = slidesWrapper.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = wrapperTop - 100;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                  }
                });
              }
            } else {
              // If no height, remove height to allow natural sizing
              slidesWrapper.style.height = '';
            }
          });
        });
      }

      updateProgressBar() {
        if (!this.progressBar || !this.progressBarFill) return;
        
        const totalSlides = this.slides.length;
        if (totalSlides === 0) return;
        
        // Build logical slide positions: cat and dog slides map to the same logical position
        const logicalPositions = [];
        let petDetailsSlideIndex = null;
        let petDetailsLogicalPosition = null;
        
        this.slides.forEach((slide, index) => {
          const isCatSlide = slide.classList.contains('feeding-calculator-slide--cat') || 
                            slide.dataset.slideType === 'cat' ||
                            slide.dataset.slideIdentifier === 'cat-details';
          const isDogSlide = slide.classList.contains('feeding-calculator-slide--dog') || 
                            slide.dataset.slideType === 'dog' ||
                            slide.dataset.slideIdentifier === 'dog-details';
          
          if (isCatSlide || isDogSlide) {
            // First pet slide (cat or dog) gets a new logical position
            if (petDetailsSlideIndex === null) {
              petDetailsSlideIndex = index;
              petDetailsLogicalPosition = logicalPositions.length;
              logicalPositions.push(index);
            }
            // Subsequent pet slides (dog if cat was first, or vice versa) map to same position
            // No need to add to logicalPositions, they share the position
          } else {
            // Non-pet slides get their own logical position
            logicalPositions.push(index);
          }
        });
        
        // Total logical positions (cat/dog count as one)
        const totalLogicalPositions = logicalPositions.length;
        
        // Find current logical position
        const currentSlide = this.slides[this.currentSlideIndex];
        const isCatSlide = currentSlide && (
          currentSlide.classList.contains('feeding-calculator-slide--cat') || 
          currentSlide.dataset.slideType === 'cat' ||
          currentSlide.dataset.slideIdentifier === 'cat-details'
        );
        const isDogSlide = currentSlide && (
          currentSlide.classList.contains('feeding-calculator-slide--dog') || 
          currentSlide.dataset.slideType === 'dog' ||
          currentSlide.dataset.slideIdentifier === 'dog-details'
        );
        
        let currentLogicalPosition;
        if (isCatSlide || isDogSlide) {
          // Cat and dog slides use the same logical position
          currentLogicalPosition = petDetailsLogicalPosition !== null ? petDetailsLogicalPosition : this.currentSlideIndex;
        } else {
          // Find the logical position for current slide
          currentLogicalPosition = logicalPositions.indexOf(this.currentSlideIndex);
          if (currentLogicalPosition === -1) {
            // Fallback: use current index if not found in logical positions
            currentLogicalPosition = this.currentSlideIndex;
          }
        }
        
        // Calculate progress: current logical position + 1 (since we're on that slide)
        // Divide by total logical positions to get percentage
        const progress = ((currentLogicalPosition + 1) / totalLogicalPositions) * 100;
        const progressPercentage = Math.min(100, Math.max(0, progress));
        
        // Update progress bar fill width
        this.progressBarFill.style.width = `${progressPercentage}%`;
        
        // Update progress text if element exists
        if (this.progressBarText) {
          this.progressBarText.textContent = `${currentLogicalPosition + 1} / ${totalLogicalPositions}`;
        }
      }

      initQuantitySelectors() {
        // Find all quantity inputs and add quantity property if not present
        // This is needed for QuantitySelector component to work properly
        const quantityInputs = this.querySelectorAll('quantity-selector input[type="number"]');
        quantityInputs.forEach((input) => {
          if (!input.hasOwnProperty('quantity')) {
            Object.defineProperty(input, 'quantity', {
              get: function() {
                return parseInt(this.value, 10) || parseInt(this.min, 10) || 1;
              },
              set: function(value) {
                const numValue = parseInt(value, 10);
                const min = parseInt(this.min, 10) || 1;
                const max = this.max ? parseInt(this.max, 10) : null;
                
                if (!isNaN(numValue)) {
                  let finalValue = Math.max(min, numValue);
                  if (max !== null) {
                    finalValue = Math.min(finalValue, max);
                  }
                  this.value = finalValue;
                }
              },
              configurable: true
            });
          }
        });
      }

      setupEventListeners() {
        if (this.isDesignMode) return;
        
        // Button clicks
        this.addEventListener('click', this.handleButtonClick.bind(this));
        
        // Form input changes (for localStorage persistence)
        this.addEventListener('input', this.boundHandleInputChange);
        this.addEventListener('change', this.boundHandleInputChange);
        
        // Form submissions
        this.addEventListener('submit', this.boundHandleFormSubmit);
        
        // Add to cart button clicks
        this.addEventListener('click', this.handleAddToCart.bind(this));
        
        // Listen for slide changes to update wrapper height
        this.addEventListener('slide:change', () => {
          setTimeout(() => {
            this.updateWrapperHeight();
          }, 100);
        });
        
        // Handle window resize to update wrapper height
        this.boundHandleResize = () => {
          this.updateWrapperHeight();
        };
        window.addEventListener('resize', this.boundHandleResize);
      }

      removeEventListeners() {
        this.removeEventListener('click', this.handleButtonClick.bind(this));
        this.removeEventListener('click', this.handleAddToCart.bind(this));
        this.removeEventListener('input', this.boundHandleInputChange);
        this.removeEventListener('change', this.boundHandleInputChange);
        this.removeEventListener('submit', this.boundHandleFormSubmit);
        if (this.boundHandleResize) {
          window.removeEventListener('resize', this.boundHandleResize);
        }
      }

      setupDesignMode() {
        // In design mode, show first slide by default
        if (this.slides.length > 0) {
          this.showSlide(0, true); // true = design mode, no transition
        }

        // Bind handlers for proper cleanup
        this.boundHandleBlockSelect = this.handleBlockSelect.bind(this);
        this.boundHandleSectionLoad = this.handleSectionLoad.bind(this);

        // Listen for block selection events
        document.addEventListener('shopify:block:select', this.boundHandleBlockSelect);
        document.addEventListener('shopify:section:load', this.boundHandleSectionLoad);
      }

      handleBlockSelect(event) {
        // Only handle events for this section
        if (event.detail.sectionId !== this.sectionId) return;

        const blockId = event.detail.blockId;
        
        // Find the slide that matches the selected block ID
        const slide = this.slides.find(s => {
          const slideBlockId = s.dataset.slideId || s.id.replace('slide-', '');
          return slideBlockId === blockId || s.id === `slide-${blockId}`;
        });
        
        if (slide) {
          const slideIndex = parseInt(slide.dataset.slideIndex, 10);
          // Show the selected slide
          this.showSlide(slideIndex, true); // true = design mode, no transition
        }
      }

      handleSectionLoad(event) {
        // Reinitialize when section is reloaded in design mode
        if (event.detail.sectionId === this.sectionId) {
          this.initSlides();
          this.setupDesignMode();
        }
      }

      handleInputChange(event) {
        // Collect form data on any input change
        this.collectFormData();
        this.persistToLocalStorage();
        
        // Clear validation errors for the changed field
        const input = event.target;
        if (input) {
          const slide = input.closest('.feeding-calculator-slide');
          if (slide) {
            const fieldName = input.name || input.id;
            if (fieldName) {
              // For radio buttons, check if any radio in the group is checked
              if (input.type === 'radio') {
                const radioGroup = slide.querySelectorAll(`input[type="radio"][name="${fieldName}"]`);
                const hasChecked = Array.from(radioGroup).some(radio => radio.checked);
                if (hasChecked) {
                  // Clear error for all radios in the group
                  radioGroup.forEach(radio => {
                    radio.classList.remove('error');
                    radio.removeAttribute('aria-invalid');
                  });
                  const errorElement = slide.querySelector(`[data-validation-error="${fieldName}"]`);
                  if (errorElement) {
                    errorElement.remove();
                  }
                }
              } else {
                // For other input types, check if field has a value
                const errorElement = slide.querySelector(`[data-validation-error="${fieldName}"]`);
                if (errorElement) {
                  const value = input.value;
                  if (value && value !== '' && (input.type !== 'number' || parseFloat(value) > 0)) {
                    // Field has a valid value, remove error
                    input.classList.remove('error');
                    input.removeAttribute('aria-invalid');
                    errorElement.remove();
                  }
                }
              }
            }
          }
        }
      }

      handleFormSubmit(event) {
        event.preventDefault();
        // Collect form data before submission
        this.collectFormData();
        this.persistToLocalStorage();
      }

      handleButtonClick(event) {
        // Find the button element first - check multiple possible selectors
        let button = event.target.closest('button, a.btn, .btn, .feeding-calculator-slide__button, .feeding-calculator-slide__nav-button, a[data-next-slide], a[data-prev-slide], a[data-pet-type], a[data-food-type]');
        
        // If not found, try finding by checking if target or parent has the attributes we're looking for
        if (!button) {
          const target = event.target;
          if (target.hasAttribute('data-pet-type') || target.hasAttribute('data-food-type') || target.hasAttribute('data-next-slide') || target.hasAttribute('data-prev-slide')) {
            button = target;
          } else {
            const parent = target.parentElement;
            if (parent && (parent.hasAttribute('data-pet-type') || parent.hasAttribute('data-food-type') || parent.hasAttribute('data-next-slide') || parent.hasAttribute('data-prev-slide'))) {
              button = parent;
            }
          }
        }
        
        if (!button) return;

        // Check if button is within this calculator component
        const calculator = button.closest('feeding-calculator');
        if (calculator !== this) return;

        // Ensure button is within a slide
        const slide = button.closest('.feeding-calculator-slide');
        if (!slide) return;

        // Handle "Start Again" / Reset button
        if (button.hasAttribute('data-reset-calculator') || 
            button.hasAttribute('data-action') && button.getAttribute('data-action') === 'reset' ||
            button.classList.contains('feeding-calculator-reset')) {
          event.preventDefault();
          event.stopPropagation();
          this.resetCalculator();
          return;
        }

        // Helper function to get attribute value (checks both getAttribute and dataset)
        const getAttr = (element, attrName) => {
          // Try getAttribute first (handles data-* attributes)
          const value = element.getAttribute(attrName);
          if (value !== null) return value;
          
          // Try dataset (converts data-pet-type to petType)
          const datasetKey = attrName.replace(/^data-/, '').replace(/-([a-z])/g, (g) => g[1].toUpperCase());
          if (element.dataset && element.dataset[datasetKey]) {
            return element.dataset[datasetKey];
          }
          
          return null;
        };

        // Check for data-prev-slide attribute
        const prevSlideTarget = getAttr(button, 'data-prev-slide');
        if (prevSlideTarget) {
          event.preventDefault();
          event.stopPropagation();

          // Special handling for "pet_type" - navigate to the appropriate pet slide
          if (prevSlideTarget === 'pet-type') {
            const petType = this.formData.pet_type;
            let targetSlide = null;
            
            if (petType === 'cat') {
              // Find cat slide
              targetSlide = this.slides.find(s => 
                s.classList.contains('feeding-calculator-slide--cat') || 
                s.dataset.slideType === 'cat' ||
                s.dataset.slideIdentifier === 'cat-details'
              );
            } else if (petType === 'dog') {
              // Find dog slide
              targetSlide = this.slides.find(s => 
                s.classList.contains('feeding-calculator-slide--dog') || 
                s.dataset.slideType === 'dog' ||
                s.dataset.slideIdentifier === 'dog-details'
              );
            }
            
            // If pet type is not set or slide not found, try to find any pet slide
            if (!targetSlide) {
              // Try cat first, then dog
              targetSlide = this.slides.find(s => 
                s.classList.contains('feeding-calculator-slide--cat') || 
                s.dataset.slideType === 'cat' ||
                s.dataset.slideIdentifier === 'cat-details'
              ) || this.slides.find(s => 
                s.classList.contains('feeding-calculator-slide--dog') || 
                s.dataset.slideType === 'dog' ||
                s.dataset.slideIdentifier === 'dog-details'
              );
            }
            
            if (targetSlide) {
              const slideIndex = parseInt(targetSlide.dataset.slideIndex, 10);
              this.navigateToSlide(slideIndex);
            }
            return; // Exit early
          }

          // Find the target slide using standard method
          const targetSlide = this.findSlide(prevSlideTarget);
          if (targetSlide) {
            const slideIndex = parseInt(targetSlide.dataset.slideIndex, 10);
            this.navigateToSlide(slideIndex);
          }
          return; // Exit early if explicit navigation is set
        }

        // Collect form data before navigation
        this.collectFormData();

        // Check for data-next-slide attribute early to infer pet_type
        const nextSlideTarget = getAttr(button, 'data-next-slide');
        
        // Validate required fields before allowing forward navigation (only for next buttons, not prev)
        if (nextSlideTarget && !this.validateSlide(slide)) {
          event.preventDefault();
          event.stopPropagation();
          return; // Stop navigation if validation fails
        }
        
        // Infer pet_type from next-slide target if it's "dog" or "cat"
        // This handles buttons with data-next-slide="dog" or data-next-slide="cat"
        if (nextSlideTarget === 'dog' || nextSlideTarget === 'cat') {
          this.formData.pet_type = nextSlideTarget;
          this.persistToLocalStorage();
          
          // Update pet type indicator in mixed products slide to trigger filtering
          this.slides.forEach(slide => {
            if (slide.classList.contains('feeding-calculator-slide--mixed-products')) {
              let petTypeIndicator = slide.querySelector('[data-pet-type-indicator]');
              if (!petTypeIndicator) {
                // Create indicator if it doesn't exist
                petTypeIndicator = document.createElement('div');
                petTypeIndicator.setAttribute('data-pet-type-indicator', '');
                petTypeIndicator.style.display = 'none';
                slide.appendChild(petTypeIndicator);
              }
              petTypeIndicator.textContent = nextSlideTarget;
              
              // If slide is already initialized, trigger filtering directly
              if (slide.dataset.mixedProductsInitialized === 'true' && slide._filterProductsByPetType) {
                slide._filterProductsByPetType();
              }
            }
          });
        }

        // Handle pet type selection (data-pet-type) - MUST happen before navigation
        // This takes precedence over inferred pet_type from next-slide
        const petType = getAttr(button, 'data-pet-type');
        if (petType) {
          this.formData.pet_type = petType;
          this.persistToLocalStorage();
          
          // Update pet type indicator in mixed products slide to trigger filtering
          this.slides.forEach(slide => {
            if (slide.classList.contains('feeding-calculator-slide--mixed-products')) {
              let petTypeIndicator = slide.querySelector('[data-pet-type-indicator]');
              if (!petTypeIndicator) {
                // Create indicator if it doesn't exist
                petTypeIndicator = document.createElement('div');
                petTypeIndicator.setAttribute('data-pet-type-indicator', '');
                petTypeIndicator.style.display = 'none';
                slide.appendChild(petTypeIndicator);
              }
              petTypeIndicator.textContent = petType;
              
              // If slide is already initialized, trigger filtering directly
              if (slide.dataset.mixedProductsInitialized === 'true' && slide._filterProductsByPetType) {
                slide._filterProductsByPetType();
              }
            }
          });
        }

        // Handle food type selection (data-food-type) - MUST happen before navigation
        const foodType = getAttr(button, 'data-food-type');
        if (foodType) {
          this.formData.food_type = foodType;
          this.persistToLocalStorage();
        }

        // Check for data-next-slide attribute
        if (nextSlideTarget) {
          event.preventDefault();
          event.stopPropagation();

          // Determine next slide dynamically or use static target
          const targetSlide = this.getNextSlide(slide, nextSlideTarget);
          if (targetSlide) {
            // Also infer pet_type from target slide type if not already set
            if (!this.formData.pet_type) {
              if (targetSlide.classList.contains('feeding-calculator-slide--dog') || 
                  targetSlide.dataset.slideType === 'dog' ||
                  targetSlide.dataset.slideIdentifier === 'dog-details') {
                this.formData.pet_type = 'dog';
                this.persistToLocalStorage();
              } else if (targetSlide.classList.contains('feeding-calculator-slide--cat') || 
                         targetSlide.dataset.slideType === 'cat' ||
                         targetSlide.dataset.slideIdentifier === 'cat-details') {
                this.formData.pet_type = 'cat';
                this.persistToLocalStorage();
              }
            }
            
            const slideIndex = parseInt(targetSlide.dataset.slideIndex, 10);
            this.navigateToSlide(slideIndex);
          }
          return; // Exit early if explicit navigation is set
        }

        // Auto-navigate for food type selection if no explicit next slide
        if (foodType) {
          const nextSlide = this.getNextSlide(slide, null);
          if (nextSlide) {
            event.preventDefault();
            event.stopPropagation();
            const slideIndex = parseInt(nextSlide.dataset.slideIndex, 10);
            this.navigateToSlide(slideIndex);
            return;
          }
        }
      }

      getNextSlide(currentSlide, staticTarget) {
        // First try static target (highest priority)
        if (staticTarget) {
          const targetSlide = this.findSlide(staticTarget);
          if (targetSlide) return targetSlide;
        }

        // Dynamic navigation based on form data and current slide
        const slideType = currentSlide?.dataset.slideType;
        const slideIdentifier = currentSlide?.dataset.slideIdentifier;
        
        // Pet selection → pet details
        if ((slideIdentifier === 'pet-selection' || slideType === 'buttons') && this.formData.pet_type) {
          if (this.formData.pet_type === 'cat') {
            const catSlide = this.slides.find(s => 
              s.dataset.slideType === 'cat' || 
              s.dataset.slideIdentifier === 'cat-details'
            );
            if (catSlide) return catSlide;
          } else if (this.formData.pet_type === 'dog') {
            const dogSlide = this.slides.find(s => 
              s.dataset.slideType === 'dog' || 
              s.dataset.slideIdentifier === 'dog-details'
            );
            if (dogSlide) return dogSlide;
          }
        }

        // Pet details → food type selection
        if ((slideType === 'cat' || slideType === 'dog' || 
             slideIdentifier === 'cat-details' || slideIdentifier === 'dog-details') && 
            this.formData.pet_type) {
          const foodTypeSlide = this.slides.find(s => 
            s.dataset.slideIdentifier === 'food-type-selection' ||
            (s.dataset.slideType === 'buttons' && s !== currentSlide)
          );
          if (foodTypeSlide) return foodTypeSlide;
        }

        // Food type selection → mixed product selection (for mixed) or skip to results (for wet/dry)
        if (slideIdentifier === 'food-type-selection' && this.formData.food_type) {
          if (this.formData.food_type === 'mixed') {
          const mixedProductSlide = this.slides.find(s => 
            s.dataset.slideIdentifier === 'mixed-product-selection' ||
            s.classList.contains('feeding-calculator-slide--mixed-products')
          );
          if (mixedProductSlide) return mixedProductSlide;
        }
        }

        // Mixed product selection → dual results
        if (slideIdentifier === 'mixed-product-selection' && 
            this.formData.selected_dry_product_id && this.formData.selected_wet_product_id) {
            const dualResultsSlide = this.slides.find(s => 
              s.dataset.slideIdentifier === 'dual-product-results' ||
              s.classList.contains('feeding-calculator-slide--results-dual')
            );
            if (dualResultsSlide) return dualResultsSlide;
        }

        // Fallback: try static target again or return null
        return staticTarget ? this.findSlide(staticTarget) : null;
      }

      navigateToSlide(index) {
        const currentSlide = this.slides[this.currentSlideIndex];
        if (currentSlide) {
          this.addToHistory(currentSlide, 'forward');
        }
        this.showSlide(index);
      }

      findSlide(target) {
        // Try to find by exact ID first
        let slide = this.querySelector(`#${target}`);
        if (slide && slide.classList.contains('feeding-calculator-slide')) return slide;

        // Try to find by data-slide-identifier
        slide = this.slides.find(s => s.dataset.slideIdentifier === target);
        if (slide) return slide;

        // Try to find by slide number pattern
        const slideNumberMatch = target.match(/slide-(\d+)/);
        if (slideNumberMatch) {
          let slideNumber = parseInt(slideNumberMatch[1], 10);
          slide = this.slides.find(s => parseInt(s.dataset.slideNumber, 10) === slideNumber);
          if (slide) return slide;
          slideNumber = slideNumber - 1;
          if (slideNumber >= 0 && slideNumber < this.slides.length) {
            return this.slides[slideNumber];
          }
        }

        // Try to find by index
        const index = parseInt(target, 10);
        if (!isNaN(index) && index >= 0 && index < this.slides.length) {
          return this.slides[index];
        }

        // Try to find by data-slide-id (the block.id)
        slide = this.slides.find(s => s.dataset.slideId === target);
        if (slide) return slide;

        return null;
      }

      findSlideById(slideId) {
        return this.slides.find(s => s.id === slideId) || 
               this.slides.find(s => `slide-${s.dataset.slideId}` === slideId);
      }

      ensureDefaultSelections(slide) {
        if (!slide) return;

        // Check for cat age select
        const catAgeSelect = slide.querySelector('select[name="cat_age"]');
        if (catAgeSelect && (!catAgeSelect.value || catAgeSelect.value === '')) {
          // Set first option as selected if no value is set
          if (catAgeSelect.options.length > 0) {
            catAgeSelect.selectedIndex = 0;
            catAgeSelect.value = catAgeSelect.options[0].value;
            // Update formData to reflect the default selection
            this.formData.cat_age = catAgeSelect.value;
            const selectedOption = catAgeSelect.options[catAgeSelect.selectedIndex];
            if (selectedOption && selectedOption.hasAttribute('data-age')) {
              this.formData.cat_age_text = selectedOption.getAttribute('data-age');
            }
          }
        }

        // Check for dog age select
        const dogAgeSelect = slide.querySelector('select[name="dog_age"]');
        if (dogAgeSelect && (!dogAgeSelect.value || dogAgeSelect.value === '')) {
          // Set first option as selected if no value is set
          if (dogAgeSelect.options.length > 0) {
            dogAgeSelect.selectedIndex = 0;
            dogAgeSelect.value = dogAgeSelect.options[0].value;
            // Update formData to reflect the default selection
            this.formData.dog_age = dogAgeSelect.value;
            const selectedOption = dogAgeSelect.options[dogAgeSelect.selectedIndex];
            if (selectedOption && selectedOption.hasAttribute('data-age')) {
              this.formData.dog_age_text = selectedOption.getAttribute('data-age');
            }
          }
        }
      }

      showSlide(index, skipTransition = false) {
        if (index < 0 || index >= this.slides.length) return;
        if (index === this.currentSlideIndex && !skipTransition) return;

        const previousSlide = this.slides[this.currentSlideIndex];
        const targetSlide = this.slides[index];

        if (!targetSlide) return;

        // Ensure default selections for age dropdowns if no saved data
        this.ensureDefaultSelections(targetSlide);

        // In design mode or when skipping transition, show immediately
        if (this.isDesignMode || skipTransition) {
          // Get previous slide height before updating (for scroll-to-top detection)
          const previousSlideHeight = previousSlide ? previousSlide.scrollHeight : null;
          
          // Hide all slides first
          this.slides.forEach(slide => {
            slide.classList.remove('feeding-calculator-slide--active');
          });
          targetSlide.classList.add('feeding-calculator-slide--active');
          this.currentSlideIndex = index;
          this.updateProgressBar();
          if (!skipTransition) {
            this.persistToLocalStorage();
          }
          
          // Update wrapper height to fit active slide
          // Pass previous slide height to enable scroll-to-top when navigating to shorter slides
          setTimeout(() => {
            this.updateWrapperHeight(previousSlideHeight);
          }, 0);
          
          // Initialize mixed products slide when it becomes active
          if (targetSlide.classList.contains('feeding-calculator-slide--mixed-products')) {
            // If already initialized, refresh calculations with latest formData
            if (targetSlide.dataset.mixedProductsInitialized === 'true' && targetSlide._refreshCalculations) {
              // Collect latest form data before refreshing
              this.collectFormData();
              targetSlide._refreshCalculations();
            } else {
              this.initMixedProductsSlide(targetSlide);
            }
          }
          
          // Populate results when results slide becomes active
          if (targetSlide.classList.contains('feeding-calculator-slide--results')) {
            // Use void to handle async without blocking
            void this.populateResultsSlide(targetSlide, targetSlide.dataset.slideId || targetSlide.id.replace('slide-', ''));
          }
          
          // Scroll to top when slide changes (skip on product pages)
          const isProductPage = document.body.classList.contains('product-template') || window.location.pathname.includes('/products/');
          if (!isProductPage) {
            requestAnimationFrame(() => {
              // Try to scroll the calculator container to top first
              const calculatorContainer = this.closest('.feeding-calculator-wrapper') || this;
              if (calculatorContainer && calculatorContainer.scrollIntoView) {
                calculatorContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
              // Also scroll window to top as fallback
              window.scrollTo({ top: 0, behavior: 'smooth' });
            });
          }
          
          return;
        }

        // Crossfade transition: use inline styles to control opacity transitions
        // This prevents flash by ensuring both slides transition smoothly
        
        // Step 1: Prepare target slide - add active class but start at opacity 0
        targetSlide.classList.add('feeding-calculator-slide--active');
        targetSlide.style.opacity = '0';
        targetSlide.style.visibility = 'visible';
        targetSlide.style.transition = 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        targetSlide.style.zIndex = '2';
        
        // Step 2: Ensure previous slide stays visible and has transition for fade out
        if (previousSlide && previousSlide !== targetSlide) {
          previousSlide.style.transition = 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
          previousSlide.style.visibility = 'visible';
          previousSlide.style.zIndex = '1';
        }
        
        // Step 3: Get previous slide height before updating index (for scroll-to-top detection)
        const previousSlideHeight = previousSlide ? previousSlide.scrollHeight : null;
        
        // Step 4: Update currentSlideIndex BEFORE measuring height so updateWrapperHeight uses the correct slide
        this.currentSlideIndex = index;
        
        // Step 5: Force reflow to ensure browser has applied initial styles
        void targetSlide.offsetHeight;
        
        // Update wrapper height immediately when slide becomes active (before transition)
        // This ensures wrapper shrinks if going to a shorter slide
        // Pass previous slide height to enable scroll-to-top when navigating to shorter slides
        this.updateWrapperHeight(previousSlideHeight);
        
        // Step 5: Use requestAnimationFrame to ensure browser has painted
        requestAnimationFrame(() => {
          // Step 6: Start crossfade - fade out previous, fade in target simultaneously
          if (previousSlide && previousSlide !== targetSlide) {
            previousSlide.style.opacity = '0';
          }
          
          // Fade in target slide
          targetSlide.style.opacity = '1';
          
          // Update progress bar and persist state (currentSlideIndex already updated above)
          this.updateProgressBar();
          this.persistToLocalStorage();
          
          // Update wrapper height again after fade starts (in case content loads)
          setTimeout(() => {
            this.updateWrapperHeight();
          }, 50);
          
          // Clean up and dispatch events after transition completes
          setTimeout(() => {
            // Remove active class from previous slide and clean up styles
            if (previousSlide && previousSlide !== targetSlide) {
              previousSlide.classList.remove('feeding-calculator-slide--active');
              previousSlide.style.opacity = '';
              previousSlide.style.visibility = '';
              previousSlide.style.transition = '';
              previousSlide.style.zIndex = '';
            }
            
            // Clean up inline styles on target slide (let CSS take over)
            targetSlide.style.opacity = '';
            targetSlide.style.visibility = '';
            targetSlide.style.transition = '';
            targetSlide.style.zIndex = '';
            
            // Ensure only target slide is active (cleanup any stray active classes)
            this.slides.forEach(slide => {
              if (slide !== targetSlide && slide.classList.contains('feeding-calculator-slide--active')) {
                slide.classList.remove('feeding-calculator-slide--active');
                slide.style.opacity = '';
                slide.style.visibility = '';
                slide.style.transition = '';
                slide.style.zIndex = '';
              }
            });
            
            // Dispatch custom event for slide change after transition
            this.dispatchEvent(new CustomEvent('slide:change', {
              detail: {
                slideIndex: index,
                slide: targetSlide,
                previousSlideIndex: previousSlide ? parseInt(previousSlide.dataset.slideIndex, 10) : null
              },
              bubbles: true
            }));
            
            // Update wrapper height to fit active slide after transition
            this.updateWrapperHeight();
            
            // Scroll to top when slide changes (skip on product pages)
            const isProductPage = document.body.classList.contains('product-template') || window.location.pathname.includes('/products/');
            if (!isProductPage) {
              requestAnimationFrame(() => {
                // Try to scroll the calculator container to top first
                const calculatorContainer = this.closest('.feeding-calculator-wrapper') || this;
                if (calculatorContainer && calculatorContainer.scrollIntoView) {
                  calculatorContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                // Also scroll window to top as fallback
                window.scrollTo({ top: 0, behavior: 'smooth' });
              });
            }
            
            // Initialize mixed products slide when it becomes active
            if (targetSlide.classList.contains('feeding-calculator-slide--mixed-products')) {
              // If already initialized, refresh calculations with latest formData
              if (targetSlide.dataset.mixedProductsInitialized === 'true' && targetSlide._refreshCalculations) {
                // Collect latest form data before refreshing
                this.collectFormData();
                targetSlide._refreshCalculations();
              } else {
                this.initMixedProductsSlide(targetSlide);
              }
            }
            
            // Populate results when results slide becomes active
            if (targetSlide.classList.contains('feeding-calculator-slide--results')) {
              void this.populateResultsSlide(targetSlide, targetSlide.dataset.slideId || targetSlide.id.replace('slide-', ''));
            }
            
          }, 400); // Match CSS transition duration
        });
      }

      initMixedProductsSlide(slide) {
        if (!slide || !slide.classList.contains('feeding-calculator-slide--mixed-products')) {
          return;
        }

        // Prevent duplicate initialization
        if (slide.dataset.mixedProductsInitialized === 'true') {
          return;
        }

        const blockId = slide.dataset.slideId || slide.id.replace('slide-', '');
        const drySelect = slide.querySelector(`#dry-product-${blockId}`);
        const wetSelect = slide.querySelector(`#wet-product-${blockId}`);
        const slider = slide.querySelector(`#mix-percentage-${blockId}`);
        const sliderSection = slide.querySelector('.feeding-calculator-slide__slider-section');
        const dryGramsValue = slide.querySelector(`#dry-grams-value-${blockId}`);
        const wetGramsValue = slide.querySelector(`#wet-grams-value-${blockId}`);
        const wetPercentage = slide.querySelector(`#wet-percentage-${blockId}`);
        const dryPercentage = slide.querySelector(`#dry-percentage-${blockId}`);
        const wetRatio = slide.querySelector(`#wet-ratio-${blockId}`);
        const nextButton = slide.querySelector('.feeding-calculator-slide__next-button');
        const dryProductImage = slide.querySelector(`#dry-product-image-img-${blockId}`);
        const wetProductImage = slide.querySelector(`#wet-product-image-img-${blockId}`);

        // Debug: Log if elements are not found
        if (!slider) console.warn('Slider not found:', `#mix-percentage-${blockId}`);
        if (!wetPercentage) console.warn('Wet percentage element not found:', `#wet-percentage-${blockId}`);
        if (!dryPercentage) console.warn('Dry percentage element not found:', `#dry-percentage-${blockId}`);
        if (!wetRatio) console.warn('Wet ratio element not found:', `#wet-ratio-${blockId}`);

        let selectedDryProduct = null;
        let selectedWetProduct = null;
        let currentPercentage = slider ? parseInt(slider.value || 50, 10) : 50;
        
        // Ensure currentPercentage is a valid number
        if (isNaN(currentPercentage)) {
          currentPercentage = 50;
        }

        // Store all options for both selects (for Safari compatibility - need to remove/add from DOM)
        // Initialize option storage if not already done
        if (!slide._dryOptionsStorage) {
          slide._dryOptionsStorage = new Map();
          if (drySelect) {
            Array.from(drySelect.options).forEach(option => {
              if (option.value) { // Store non-placeholder options
                slide._dryOptionsStorage.set(option.value, {
                  element: option.cloneNode(true),
                  petType: option.getAttribute('data-pet-type') || ''
                });
              }
            });
          }
        }
        
        if (!slide._wetOptionsStorage) {
          slide._wetOptionsStorage = new Map();
          if (wetSelect) {
            Array.from(wetSelect.options).forEach(option => {
              if (option.value) { // Store non-placeholder options
                slide._wetOptionsStorage.set(option.value, {
                  element: option.cloneNode(true),
                  petType: option.getAttribute('data-pet-type') || ''
                });
              }
            });
          }
        }

        const filterProductsByPetType = () => {
          // Try to get pet_type from formData
          let petType = this.formData.pet_type;
          
          // If pet_type is not set, try to infer it from form data
          if (!petType) {
            if (this.formData.cat_weight || this.formData.cat_age || this.formData.cat_activity !== undefined) {
              petType = 'cat';
              this.formData.pet_type = 'cat';
              this.persistToLocalStorage();
            } else if (this.formData.dog_weight || this.formData.dog_age || this.formData.dog_activity !== undefined) {
              petType = 'dog';
              this.formData.pet_type = 'dog';
              this.persistToLocalStorage();
            }
          }
          
          // If still not set, try to infer from current slide
          if (!petType) {
            const currentSlide = this.slides[this.currentSlideIndex];
            if (currentSlide) {
              if (currentSlide.classList.contains('feeding-calculator-slide--cat') || 
                  currentSlide.dataset.slideType === 'cat' ||
                  currentSlide.dataset.slideIdentifier === 'cat-details') {
                petType = 'cat';
                this.formData.pet_type = 'cat';
                this.persistToLocalStorage();
              } else if (currentSlide.classList.contains('feeding-calculator-slide--dog') || 
                         currentSlide.dataset.slideType === 'dog' ||
                         currentSlide.dataset.slideIdentifier === 'dog-details') {
                petType = 'dog';
                this.formData.pet_type = 'dog';
                this.persistToLocalStorage();
              }
            }
          }
          
          if (!petType) {
            console.warn('pet_type not set, cannot filter products');
            return;
          }

          // Filter dry products - Safari-compatible: remove options from DOM instead of hiding
          if (drySelect) {
            let needsDryDefault = false;
            const currentSelectedValue = drySelect.value;
            
            // Store placeholder if it exists (before removing anything)
            const placeholderDry = drySelect.querySelector('option[value=""]');
            const placeholderClone = placeholderDry ? placeholderDry.cloneNode(true) : null;
            
            // Remove ALL options from DOM (including placeholder) to avoid duplicates
            drySelect.innerHTML = '';
            
            // Re-add placeholder first if it exists, otherwise create one
            if (placeholderClone) {
              drySelect.appendChild(placeholderClone);
            } else {
              // Create placeholder if it doesn't exist
              const newPlaceholder = document.createElement('option');
              newPlaceholder.value = '';
              newPlaceholder.textContent = 'Select Dry food';
              newPlaceholder.disabled = true;
              drySelect.appendChild(newPlaceholder);
            }
            
            // Re-add only matching options
            let visibleCount = 0;
            slide._dryOptionsStorage.forEach((optionData, value) => {
              if (optionData.petType === petType) {
                const optionClone = optionData.element.cloneNode(true);
                drySelect.appendChild(optionClone);
                visibleCount++;
              }
            });
            
            // Check if previously selected option still exists (matches pet type)
            if (currentSelectedValue) {
              const stillExists = Array.from(drySelect.options).some(opt => opt.value === currentSelectedValue);
              if (!stillExists) {
                needsDryDefault = true;
              } else {
                // Restore selection
                drySelect.value = currentSelectedValue;
              }
            }
            
            // If no product is selected or selection was cleared, ensure placeholder is selected
            if (needsDryDefault || !drySelect.value) {
              drySelect.value = '';
              if (placeholderClone || placeholderDry) {
                const currentPlaceholder = drySelect.querySelector('option[value=""]');
                if (currentPlaceholder) {
                  currentPlaceholder.selected = true;
                }
              }
              selectedDryProduct = null;
              updateProductImage(dryProductImage, '', '');
              delete this.formData.selected_dry_product_id;
              delete this.formData.selected_dry_variant_id;
              delete this.formData.selected_dry_product_data;
            }
          }

          // Filter wet products - Safari-compatible: remove options from DOM instead of hiding
          if (wetSelect) {
            let needsWetDefault = false;
            const currentSelectedValue = wetSelect.value;
            
            // Store placeholder if it exists (before removing anything)
            const placeholderWet = wetSelect.querySelector('option[value=""]');
            const placeholderClone = placeholderWet ? placeholderWet.cloneNode(true) : null;
            
            // Remove ALL options from DOM (including placeholder) to avoid duplicates
            wetSelect.innerHTML = '';
            
            // Re-add placeholder first if it exists, otherwise create one
            if (placeholderClone) {
              wetSelect.appendChild(placeholderClone);
            } else {
              // Create placeholder if it doesn't exist
              const newPlaceholder = document.createElement('option');
              newPlaceholder.value = '';
              newPlaceholder.textContent = 'Select Wet food';
              newPlaceholder.disabled = true;
              wetSelect.appendChild(newPlaceholder);
            }
            
            // Re-add only matching options
            let visibleCount = 0;
            slide._wetOptionsStorage.forEach((optionData, value) => {
              if (optionData.petType === petType) {
                const optionClone = optionData.element.cloneNode(true);
                wetSelect.appendChild(optionClone);
                visibleCount++;
              }
            });
            
            // Check if previously selected option still exists (matches pet type)
            if (currentSelectedValue) {
              const stillExists = Array.from(wetSelect.options).some(opt => opt.value === currentSelectedValue);
              if (!stillExists) {
                needsWetDefault = true;
              } else {
                // Restore selection
                wetSelect.value = currentSelectedValue;
              }
            }
            
            // If no product is selected or selection was cleared, ensure placeholder is selected
            if (needsWetDefault || !wetSelect.value) {
              wetSelect.value = '';
              if (placeholderClone || placeholderWet) {
                const currentPlaceholder = wetSelect.querySelector('option[value=""]');
                if (currentPlaceholder) {
                  currentPlaceholder.selected = true;
                }
              }
              selectedWetProduct = null;
              updateProductImage(wetProductImage, '', '');
              delete this.formData.selected_wet_product_id;
              delete this.formData.selected_wet_variant_id;
              delete this.formData.selected_wet_product_data;
            }
          }

          // Update grams display and slider visibility after filtering
          updateGramsDisplay();
          updateSliderVisibility();
        };

        const extractProductData = (option) => {
          if (!option || !option.value) return null;
          
          return {
            id: option.value,
            variant_id: option.getAttribute('data-variant-id') || '',
            variant_title: option.getAttribute('data-variant-title') || '',
            title: option.getAttribute('data-product-title') || option.textContent.trim(),
            image: option.getAttribute('data-product-image') || '',
            price: parseInt(option.getAttribute('data-product-price') || '0', 10),
            weight_grams: parseFloat(option.getAttribute('data-product-weight') || '0'),
            calories: parseFloat(option.getAttribute('data-product-calories') || '0'),
            category: option.getAttribute('data-product-category') || '',
            food_type: option.getAttribute('data-product-food-type') || '',
            feeding_unit_grams: parseFloat(option.getAttribute('data-product-feeding-unit-grams') || '0'),
            handle: option.getAttribute('data-product-handle') || ''
          };
        };

        const updateProductImage = (imageElement, imageUrl, productTitle) => {
          if (!imageElement) return;
          
          if (imageUrl && imageUrl.trim() !== '') {
            imageElement.src = imageUrl;
            imageElement.alt = productTitle || '';
            imageElement.style.display = 'block';
          } else {
            imageElement.src = '';
            imageElement.alt = '';
            imageElement.style.display = 'none';
          }
        };

        const updateGramsDisplay = () => {
          // Check if both products are selected
          const bothSelected = selectedDryProduct && selectedWetProduct;
          
          // If slider is visible, use slider ratios; otherwise use 100% for single product
          // Slider at 0 (left/WET) = 100% wet, slider at 100 (right/DRY) = 100% dry
          const wetRatio = slider && sliderSection && sliderSection.style.display !== 'none' ? (100 - currentPercentage) : (selectedWetProduct && !selectedDryProduct ? 100 : 0);
          const dryRatio = slider && sliderSection && sliderSection.style.display !== 'none' ? currentPercentage : (selectedDryProduct && !selectedWetProduct ? 100 : 0);

          // Store ratios in formData
          this.formData.wet_ratio = wetRatio;
          this.formData.dry_ratio = dryRatio;

          // Only calculate if we have required products selected
          const needsDry = dryRatio > 0;
          const needsWet = wetRatio > 0;

          // Store product data in formData for calculations
          if (needsDry && selectedDryProduct) {
            this.formData.selected_dry_product_data = selectedDryProduct;
          } else {
            delete this.formData.selected_dry_product_data;
          }

          if (needsWet && selectedWetProduct) {
            this.formData.selected_wet_product_data = selectedWetProduct;
          } else {
            delete this.formData.selected_wet_product_data;
          }

          // Calculate grams based on selection state
          if (bothSelected && slider && sliderSection && sliderSection.style.display !== 'none') {
            // Both selected and slider visible - use mixed calculation
            // If ratio is 0% for a product, explicitly set grams to 0
            if (dryRatio === 0) {
              // 100% wet - dry grams should be 0
              if (dryGramsValue) dryGramsValue.textContent = '0';
              // Calculate wet grams using single product calculation
              const originalProductData = this.formData.selected_product_data;
              this.formData.selected_product_data = selectedWetProduct;
              const wetGrams = this.calculateDailyGrams ? this.calculateDailyGrams() : 0;
              this.formData.selected_product_data = originalProductData;
              if (wetGramsValue) wetGramsValue.textContent = Math.round(wetGrams);
            } else if (wetRatio === 0) {
              // 100% dry - wet grams should be 0
              if (wetGramsValue) wetGramsValue.textContent = '0';
              // Calculate dry grams using single product calculation
              const originalProductData = this.formData.selected_product_data;
              this.formData.selected_product_data = selectedDryProduct;
              const dryGrams = this.calculateDailyGrams ? this.calculateDailyGrams() : 0;
              this.formData.selected_product_data = originalProductData;
              if (dryGramsValue) dryGramsValue.textContent = Math.round(dryGrams);
            } else {
              // Mixed ratio - use mixed calculation
              const result = this.calculateDailyGrams();
              if (typeof result === 'object' && result.wet !== undefined) {
                if (dryGramsValue) dryGramsValue.textContent = result.dry || 0;
                if (wetGramsValue) wetGramsValue.textContent = result.wet || 0;
              }
            }
          } else if (selectedDryProduct && !selectedWetProduct) {
            // Only dry selected - use single product calculation
            const originalProductData = this.formData.selected_product_data;
            this.formData.selected_product_data = selectedDryProduct;
            const grams = this.calculateDailyGrams ? this.calculateDailyGrams() : 0;
            this.formData.selected_product_data = originalProductData;
            if (dryGramsValue) dryGramsValue.textContent = Math.round(grams);
            if (wetGramsValue) wetGramsValue.textContent = '0';
          } else if (selectedWetProduct && !selectedDryProduct) {
            // Only wet selected - use single product calculation
            const originalProductData = this.formData.selected_product_data;
            this.formData.selected_product_data = selectedWetProduct;
            const grams = this.calculateDailyGrams ? this.calculateDailyGrams() : 0;
            this.formData.selected_product_data = originalProductData;
            if (wetGramsValue) wetGramsValue.textContent = Math.round(grams);
            if (dryGramsValue) dryGramsValue.textContent = '0';
          } else {
            // No products selected or calculation not available
            if (dryGramsValue) dryGramsValue.textContent = '0';
            if (wetGramsValue) wetGramsValue.textContent = '0';
          }

          // Update validation and slider visibility (skip if already updating to prevent circular calls)
          updateValidation();
          if (!slide._updatingSliderVisibility) {
            updateSliderVisibility();
          }
        };

        const updateSliderDisplay = () => {
          // Slider at 0 (left/WET) = 100% wet, slider at 100 (right/DRY) = 100% dry
          const wet = isNaN(currentPercentage) ? 100 : Math.max(0, Math.min(100, 100 - currentPercentage));
          const dry = isNaN(currentPercentage) ? 0 : Math.max(0, Math.min(100, currentPercentage));
          
          // Re-query elements to ensure they exist (in case DOM changed)
          const wetPctEl = slide.querySelector(`#wet-percentage-${blockId}`);
          const dryPctEl = slide.querySelector(`#dry-percentage-${blockId}`);
          const wetRatioEl = slide.querySelector(`#wet-ratio-${blockId}`);
          
          if (wetPctEl) {
            wetPctEl.textContent = `${wet}%`;
          }
          if (dryPctEl) {
            dryPctEl.textContent = `${dry}%`;
          }
          if (wetRatioEl) {
            wetRatioEl.textContent = `${wet}/${dry}`;
          }
        };

        const updateSliderVisibility = () => {
          const sliderSection = slide.querySelector('.feeding-calculator-slide__slider-section');
          if (!sliderSection) return;
          
          // Show slider only when both products are selected
          const bothSelected = selectedDryProduct && selectedWetProduct;
          const wasHidden = sliderSection.style.display === 'none' || sliderSection.style.display === '';
          sliderSection.style.display = bothSelected ? 'block' : 'none';
          
          // If slider just became visible (both products selected), initialize to 50/50
          // Use a flag to ensure we only initialize once when slider becomes visible
          if (bothSelected && slider && wasHidden && !slide._sliderInitialized) {
            // Always initialize to 50/50 when slider becomes visible
            currentPercentage = 50;
            slider.value = 50;
            
            // Update formData to reflect 50/50 ratio
            this.formData.wet_ratio = 50;
            this.formData.dry_ratio = 50;
            
            // Mark slider as initialized
            slide._sliderInitialized = true;
            
            // Update slider display
            updateSliderDisplay();
            
            // Trigger grams recalculation by calling updateGramsDisplay
            // But prevent circular call by temporarily skipping updateSliderVisibility
            // We'll use a flag to track if we're already updating
            if (!slide._updatingSliderVisibility) {
              slide._updatingSliderVisibility = true;
              updateGramsDisplay();
              slide._updatingSliderVisibility = false;
            }
            
            // Persist the 50/50 ratio to localStorage
            this.persistToLocalStorage();
          } else if (!bothSelected) {
            // Reset initialization flag when slider is hidden (products deselected)
            slide._sliderInitialized = false;
          }
        };

        const updateValidation = () => {
          const sliderSection = slide.querySelector('.feeding-calculator-slide__slider-section');
          const isSliderVisible = sliderSection && sliderSection.style.display !== 'none';
          
          // Slider at 0 (left/WET) = 100% wet, slider at 100 (right/DRY) = 100% dry
          const wetRatio = isNaN(currentPercentage) ? 50 : (100 - currentPercentage);
          const dryRatio = isNaN(currentPercentage) ? 50 : currentPercentage;

          // Update required attributes based on slider position
          // Only disable if slider section is visible and slider is at 0% for that product type
          if (drySelect) {
            if (isSliderVisible && slider && dryRatio === 0) {
              // At 0% dry (100% wet) - dry product not needed
              drySelect.removeAttribute('required');
              drySelect.setAttribute('disabled', 'disabled');
            } else {
              // Dry product is needed or slider not visible - keep enabled
              drySelect.setAttribute('required', 'required');
              drySelect.removeAttribute('disabled');
            }
          }

          if (wetSelect) {
            if (isSliderVisible && slider && wetRatio === 0) {
              // At 0% wet (100% dry) - wet product not needed
              wetSelect.removeAttribute('required');
              wetSelect.setAttribute('disabled', 'disabled');
            } else {
              // Wet product is needed or slider not visible - keep enabled
              wetSelect.setAttribute('required', 'required');
              wetSelect.removeAttribute('disabled');
            }
          }

          // Enable/disable next button based on validation
          // For single product selection, at least one must be selected
          // For mixed (both selected), slider validation applies
          if (nextButton) {
            const bothSelected = selectedDryProduct && selectedWetProduct;
            let isValid = false;
            
            if (bothSelected) {
              // Both selected - use slider-based validation
              isValid = 
                (dryRatio === 0 || (dryRatio > 0 && selectedDryProduct)) &&
                (wetRatio === 0 || (wetRatio > 0 && selectedWetProduct));
            } else {
              // Single product - at least one must be selected
              isValid = selectedDryProduct || selectedWetProduct;
            }
            
            nextButton.disabled = !isValid;
          }
        };

        // Dry product selection
        if (drySelect) {
          drySelect.addEventListener('change', function(event) {
            const select = event.target;
            const selectedIndex = select.selectedIndex;
            
            if (selectedIndex < 0 || !select.options || !select.options[selectedIndex] || !select.value) {
              selectedDryProduct = null;
              delete this.formData.selected_dry_product_id;
              delete this.formData.selected_dry_variant_id;
              delete this.formData.selected_dry_product_data;
              updateProductImage(dryProductImage, '', '');
              this.persistToLocalStorage();
              updateGramsDisplay();
              updateSliderVisibility();
              return;
            }

            const selectedOption = select.options[selectedIndex];
            selectedDryProduct = extractProductData(selectedOption);
            
            if (selectedDryProduct) {
              this.formData.selected_dry_product_id = selectedDryProduct.id;
              this.formData.selected_dry_variant_id = selectedDryProduct.variant_id;
              this.formData.selected_dry_product_data = selectedDryProduct;
              updateProductImage(dryProductImage, selectedDryProduct.image, selectedDryProduct.title);
            } else {
              delete this.formData.selected_dry_product_id;
              delete this.formData.selected_dry_variant_id;
              delete this.formData.selected_dry_product_data;
              updateProductImage(dryProductImage, '', '');
            }
            this.persistToLocalStorage();
            updateGramsDisplay();
            updateSliderVisibility();
          }.bind(this));
        }

        // Wet product selection
        if (wetSelect) {
          wetSelect.addEventListener('change', function(event) {
            const select = event.target;
            const selectedIndex = select.selectedIndex;
            
            if (selectedIndex < 0 || !select.options || !select.options[selectedIndex] || !select.value) {
              selectedWetProduct = null;
              delete this.formData.selected_wet_product_id;
              delete this.formData.selected_wet_variant_id;
              delete this.formData.selected_wet_product_data;
              updateProductImage(wetProductImage, '', '');
              this.persistToLocalStorage();
              updateGramsDisplay();
              updateSliderVisibility();
              return;
            }
            
            const selectedOption = select.options[selectedIndex];
            selectedWetProduct = extractProductData(selectedOption);
            
            if (selectedWetProduct) {
              this.formData.selected_wet_product_id = selectedWetProduct.id;
              this.formData.selected_wet_variant_id = selectedWetProduct.variant_id;
              this.formData.selected_wet_product_data = selectedWetProduct;
              updateProductImage(wetProductImage, selectedWetProduct.image, selectedWetProduct.title);
            } else {
              delete this.formData.selected_wet_product_id;
              delete this.formData.selected_wet_variant_id;
              delete this.formData.selected_wet_product_data;
              updateProductImage(wetProductImage, '', '');
            }
            this.persistToLocalStorage();
            updateGramsDisplay();
            updateSliderVisibility();
          }.bind(this));
        }

        // Slider change
        if (slider) {
          slider.addEventListener('input', function(event) {
            const sliderEl = event.target;
            const newValue = parseInt(sliderEl.value, 10);
            if (!isNaN(newValue)) {
              currentPercentage = Math.max(0, Math.min(100, newValue));
              updateSliderDisplay();
              updateGramsDisplay();
              
              // Slider at 0 (left/WET) = 100% wet, slider at 100 (right/DRY) = 100% dry
              this.formData.wet_ratio = 100 - currentPercentage;
              this.formData.dry_ratio = currentPercentage;
              this.persistToLocalStorage();
            }
          }.bind(this));
          
          // Also listen for change event (for when user releases slider)
          slider.addEventListener('change', function(event) {
            const sliderEl = event.target;
            const newValue = parseInt(sliderEl.value, 10);
            if (!isNaN(newValue)) {
              currentPercentage = Math.max(0, Math.min(100, newValue));
              updateSliderDisplay();
              updateGramsDisplay();
            }
          }.bind(this));
        }

        // Store filter function reference on slide for external calls
        slide._filterProductsByPetType = filterProductsByPetType;
        
        // Create refresh function that updates product selections and recalculates
        const refreshCalculations = () => {
          // Update currentPercentage from slider
          if (slider) {
            const sliderValue = parseInt(slider.value || 50, 10);
            if (!isNaN(sliderValue)) {
              currentPercentage = Math.max(0, Math.min(100, sliderValue));
            }
          }
          
          // Refresh product selections from DOM
          // Note: After DOM removal filtering, if option exists in DOM, it's valid (matches pet type)
          if (drySelect && drySelect.value) {
            const dryOption = drySelect.options[drySelect.selectedIndex];
            if (dryOption && dryOption.value) {
              selectedDryProduct = extractProductData(dryOption);
              updateProductImage(dryProductImage, selectedDryProduct?.image || '', selectedDryProduct?.title || '');
            } else {
              selectedDryProduct = null;
              updateProductImage(dryProductImage, '', '');
            }
          } else {
            selectedDryProduct = null;
            updateProductImage(dryProductImage, '', '');
          }
          
          if (wetSelect && wetSelect.value) {
            const wetOption = wetSelect.options[wetSelect.selectedIndex];
            if (wetOption && wetOption.value) {
              selectedWetProduct = extractProductData(wetOption);
              updateProductImage(wetProductImage, selectedWetProduct?.image || '', selectedWetProduct?.title || '');
            } else {
              selectedWetProduct = null;
              updateProductImage(wetProductImage, '', '');
            }
          } else {
            selectedWetProduct = null;
            updateProductImage(wetProductImage, '', '');
          }
          
          // Recalculate grams display with updated data
          updateGramsDisplay();
          updateSliderVisibility();
        };
        
        // Store refresh function reference on slide for external calls
        slide._refreshCalculations = refreshCalculations;
        slide._updateGramsDisplay = updateGramsDisplay;
        
        // Filter products by pet type first
        filterProductsByPetType();

        // Initialize display - only if slider exists
        if (slider) {
          // Ensure slider value matches currentPercentage
          const sliderValue = parseInt(slider.value || 50, 10);
          if (!isNaN(sliderValue)) {
            currentPercentage = Math.max(0, Math.min(100, sliderValue));
            slider.value = currentPercentage;
          }
          updateSliderDisplay();
        }
        updateValidation();

        // Restore from localStorage if available, otherwise use defaults
        // Since wet_ratio = 100 - currentPercentage, restore: currentPercentage = 100 - wet_ratio
        if (this.formData.wet_ratio !== undefined && slider) {
          const restoredWetRatio = parseInt(this.formData.wet_ratio, 10);
          if (!isNaN(restoredWetRatio)) {
            // Convert stored wet_ratio back to slider position
            currentPercentage = 100 - restoredWetRatio;
            slider.value = currentPercentage;
            updateSliderDisplay();
          }
        }
        
        // Initialize dry product selection - restore from localStorage if available
        // Note: After DOM removal filtering, if option exists in DOM, it's valid (matches pet type)
        if (this.formData.selected_dry_product_id && drySelect) {
          // Check if the stored product ID exists in the filtered options
          const optionExists = Array.from(drySelect.options).some(opt => opt.value === this.formData.selected_dry_product_id);
          if (optionExists) {
            drySelect.value = this.formData.selected_dry_product_id;
            const dryOption = drySelect.options[drySelect.selectedIndex];
            if (dryOption && dryOption.value) {
              selectedDryProduct = extractProductData(dryOption);
              updateProductImage(dryProductImage, selectedDryProduct?.image || '', selectedDryProduct?.title || '');
            }
          } else {
            // Clear if restored selection doesn't match pet type - select placeholder
            const placeholderDry = drySelect.querySelector('option[value=""]');
            if (placeholderDry) {
              drySelect.value = '';
              placeholderDry.selected = true;
            }
            selectedDryProduct = null;
            updateProductImage(dryProductImage, '', '');
            delete this.formData.selected_dry_product_id;
            delete this.formData.selected_dry_variant_id;
            delete this.formData.selected_dry_product_data;
          }
        } else if (drySelect) {
          // No saved selection - ensure placeholder is selected
          const placeholderDry = drySelect.querySelector('option[value=""]');
          if (placeholderDry) {
            drySelect.value = '';
            placeholderDry.selected = true;
          }
          selectedDryProduct = null;
        }
        
        // Initialize wet product selection - restore from localStorage if available
        // Note: After DOM removal filtering, if option exists in DOM, it's valid (matches pet type)
        if (this.formData.selected_wet_product_id && wetSelect) {
          // Check if the stored product ID exists in the filtered options
          const optionExists = Array.from(wetSelect.options).some(opt => opt.value === this.formData.selected_wet_product_id);
          if (optionExists) {
            wetSelect.value = this.formData.selected_wet_product_id;
            const wetOption = wetSelect.options[wetSelect.selectedIndex];
            if (wetOption && wetOption.value) {
              selectedWetProduct = extractProductData(wetOption);
              updateProductImage(wetProductImage, selectedWetProduct?.image || '', selectedWetProduct?.title || '');
            }
          } else {
            // Clear if restored selection doesn't match pet type - select placeholder
            const placeholderWet = wetSelect.querySelector('option[value=""]');
            if (placeholderWet) {
              wetSelect.value = '';
              placeholderWet.selected = true;
            }
            selectedWetProduct = null;
            updateProductImage(wetProductImage, '', '');
            delete this.formData.selected_wet_product_id;
            delete this.formData.selected_wet_variant_id;
            delete this.formData.selected_wet_product_data;
          }
        } else if (wetSelect) {
          // No saved selection - ensure placeholder is selected
          const placeholderWet = wetSelect.querySelector('option[value=""]');
          if (placeholderWet) {
            wetSelect.value = '';
            placeholderWet.selected = true;
          }
          selectedWetProduct = null;
        }
        
        // Update grams display and slider visibility after initializing selections
        updateGramsDisplay();
        updateSliderVisibility();

        // Listen for pet type changes (in case user navigates back and changes pet type)
        // Create a hidden indicator element to watch for pet type changes
        let petTypeIndicator = slide.querySelector('[data-pet-type-indicator]');
        if (!petTypeIndicator) {
          petTypeIndicator = document.createElement('div');
          petTypeIndicator.setAttribute('data-pet-type-indicator', '');
          petTypeIndicator.style.display = 'none';
          slide.appendChild(petTypeIndicator);
        }
        petTypeIndicator.textContent = this.formData.pet_type || '';
        
        // Watch for changes to formData.pet_type
        const petTypeObserver = new MutationObserver(() => {
          filterProductsByPetType();
        });
        petTypeObserver.observe(petTypeIndicator, { childList: true, characterData: true, subtree: true });

        // Store observer reference for cleanup if needed
        slide._petTypeObserver = petTypeObserver;

        // Mark as initialized to prevent duplicate event listeners
        slide.dataset.mixedProductsInitialized = 'true';
      }

      // State Management Methods

      collectFormData() {
        // Collect from all forms/inputs in the calculator
        const forms = this.querySelectorAll('form');
        const standaloneInputs = this.querySelectorAll('.feeding-calculator-slide input, .feeding-calculator-slide select, .feeding-calculator-slide textarea');
        
        // Process form inputs
        forms.forEach(form => {
          const inputs = form.querySelectorAll('input, select, textarea');
          inputs.forEach(input => {
            this.collectInputValue(input);
          });
        });

        // Process standalone inputs (not in forms)
        standaloneInputs.forEach(input => {
          if (!input.closest('form')) {
            this.collectInputValue(input);
          }
        });

        // Collect data from data attributes on buttons/inputs
        // Check ALL slides, not just current one, to ensure we get all form data
        this.slides.forEach(slide => {
          const dataInputs = slide.querySelectorAll('[data-dog-weight], [data-cat-weight], [data-dog-age], [data-cat-age], [data-dog-activity], [data-cat-activity], [data-treats]');
          
          // Handle radio button groups separately to ensure we clear values if none are checked
          const radioGroups = new Set();
          dataInputs.forEach(input => {
            if (input.type === 'radio') {
              radioGroups.add(input.name);
            }
          });
          
          // Process radio groups - check if any is checked, otherwise clear the value
          radioGroups.forEach(radioName => {
            const checkedRadio = slide.querySelector(`input[type="radio"][name="${radioName}"]:checked`);
            if (checkedRadio) {
              // For activity radio buttons, reconstruct full "Text_Value" format for consistency with module parsing
              // The radio value is just the numeric part (e.g., "165.0"), but we need full format (e.g., "Athlete_165.0")
              if (radioName === 'cat_activity' || radioName === 'dog_activity') {
                const activityText = checkedRadio.getAttribute('data-activity') || '';
                const activityValue = checkedRadio.value || '';
                // Reconstruct full format: "Text_Value" (e.g., "Athlete_165.0")
                if (activityText && activityValue) {
                  this.formData[radioName] = `${activityText}_${activityValue}`;
                } else {
                  // Fallback: use just the value if data-activity not available
                  this.formData[radioName] = activityValue;
                }
              } else {
                this.formData[radioName] = checkedRadio.value;
              }
            } else {
              // Clear the value if no radio button in this group is checked
              delete this.formData[radioName];
            }
          });
          
          // Process other inputs
          dataInputs.forEach(input => {
            const name = input.name || input.id;
            if (name && input.type !== 'radio') {
              if (input.type === 'checkbox') {
                this.formData[name] = input.checked;
              } else if (input.value) {
                this.formData[name] = input.value;
                
                // Store age text (data-age attribute) for age dropdowns
                if ((name === 'cat_age' || name === 'dog_age') && input.tagName === 'SELECT') {
                  const selectedOption = input.options[input.selectedIndex];
                  if (selectedOption && selectedOption.hasAttribute('data-age')) {
                    this.formData[name + '_text'] = selectedOption.getAttribute('data-age');
                  }
                }
              }
            }
          });
          });

          // Infer pet_type from slide type if not already set
        const currentSlide = this.slides[this.currentSlideIndex];
        if (currentSlide && !this.formData.pet_type) {
            if (currentSlide.classList.contains('feeding-calculator-slide--cat') || 
                currentSlide.dataset.slideType === 'cat' ||
                currentSlide.dataset.slideIdentifier === 'cat-details') {
              this.formData.pet_type = 'cat';
            } else if (currentSlide.classList.contains('feeding-calculator-slide--dog') || 
                       currentSlide.dataset.slideType === 'dog' ||
                       currentSlide.dataset.slideIdentifier === 'dog-details') {
              this.formData.pet_type = 'dog';
            }
          }

          // Infer pet_type from form field names if not set
          if (!this.formData.pet_type) {
            if (this.formData.cat_weight || this.formData.cat_age || this.formData.cat_activity !== undefined) {
              this.formData.pet_type = 'cat';
            } else if (this.formData.dog_weight || this.formData.dog_age || this.formData.dog_activity !== undefined) {
              this.formData.pet_type = 'dog';
          }
        }
      }

      collectInputValue(input) {
        const name = input.name || input.id;
        if (!name) return;

        // Handle different input types
        if (input.type === 'checkbox') {
          this.formData[name] = input.checked;
        } else if (input.type === 'radio') {
          // For radio buttons, check if any in the group is checked
          const slide = input.closest('.feeding-calculator-slide');
          if (slide) {
            const checkedRadio = slide.querySelector(`input[type="radio"][name="${name}"]:checked`);
            if (checkedRadio) {
              this.formData[name] = checkedRadio.value;
            } else {
              // Clear the value if no radio button in this group is checked
              delete this.formData[name];
            }
          } else if (input.checked) {
            // Fallback if slide not found
            this.formData[name] = input.value;
          } else {
            // Clear if not checked and no other in group is checked
            delete this.formData[name];
          }
        } else if (input.type === 'number') {
          this.formData[name] = parseFloat(input.value) || 0;
        } else if (input.tagName === 'SELECT') {
          this.formData[name] = input.value;
          
          // Store age text (data-age attribute) for age dropdowns
          if ((name === 'cat_age' || name === 'dog_age')) {
            const selectedOption = input.options[input.selectedIndex];
            if (selectedOption && selectedOption.hasAttribute('data-age')) {
              this.formData[name + '_text'] = selectedOption.getAttribute('data-age');
            }
          }
        } else if (input.value !== undefined && input.value !== '') {
          const value = parseFloat(input.value);
          this.formData[name] = isNaN(value) ? '' : value;
        } else {
          this.formData[name] = input.value || '';
        }
      }

      validateSlide(slide) {
        // Check if this is a cat slide
        if (slide.classList.contains('feeding-calculator-slide--cat') || 
            slide.dataset.slideType === 'cat' ||
            slide.dataset.slideIdentifier === 'cat-details') {
          
          // Validate cat_weight - must be filled and > 0
          const catWeight = this.formData.cat_weight;
          if (!catWeight || catWeight === '' || parseFloat(catWeight) <= 0) {
            this.showValidationError(slide, 'cat_weight', 'Please enter your cat\'s weight');
            return false;
          }
          
          // Validate cat_age - must be selected
          const catAge = this.formData.cat_age;
          if (!catAge || catAge === '') {
            this.showValidationError(slide, 'cat_age', 'Please select your cat\'s age');
            return false;
          }
          
          // Validate cat_activity - must be selected (one radio button checked)
          const catActivity = this.formData.cat_activity;
          if (!catActivity || catActivity === '') {
            this.showValidationError(slide, 'cat_activity', 'Please select your cat\'s activity level');
            return false;
          }
          
          // Clear any previous errors if validation passes
          this.clearValidationErrors(slide);
        }
        
        // Check if this is a dog slide
        if (slide.classList.contains('feeding-calculator-slide--dog') || 
            slide.dataset.slideType === 'dog' ||
            slide.dataset.slideIdentifier === 'dog-details') {
          
          // Validate dog_weight - must be filled and > 0
          const dogWeight = this.formData.dog_weight;
          if (!dogWeight || dogWeight === '' || parseFloat(dogWeight) <= 0) {
            this.showValidationError(slide, 'dog_weight', 'Please enter your dog\'s weight');
            return false;
          }
          
          // Validate dog_age - must be selected
          const dogAge = this.formData.dog_age;
          if (!dogAge || dogAge === '') {
            this.showValidationError(slide, 'dog_age', 'Please select your dog\'s age');
            return false;
          }
          
          // Validate dog_activity - must be selected (one radio button checked)
          const dogActivity = this.formData.dog_activity;
          if (!dogActivity || dogActivity === '') {
            this.showValidationError(slide, 'dog_activity', 'Please select your dog\'s activity level');
            return false;
          }
          
          // Clear any previous errors if validation passes
          this.clearValidationErrors(slide);
        }
        
        return true; // Validation passed
      }

      showValidationError(slide, fieldName, message) {
        // Find the field element
        const field = slide.querySelector(`[name="${fieldName}"]`);
        if (!field) return;
        
        // Remove existing error message
        const existingError = slide.querySelector(`[data-validation-error="${fieldName}"]`);
        if (existingError) {
          existingError.remove();
        }
        
        // Check if this is a radio button field (activity)
        const isRadioField = field.type === 'radio' && (fieldName === 'cat_activity' || fieldName === 'dog_activity');
        
        if (isRadioField) {
          // For radio buttons, find the activity-radios container
          const activityRadiosContainer = slide.querySelector('.feeding-calculator-slide__activity-radios');
          if (!activityRadiosContainer) return;
          
          // Add error class to all radio buttons in the group
          const radioGroup = slide.querySelectorAll(`input[type="radio"][name="${fieldName}"]`);
          radioGroup.forEach(radio => {
            radio.classList.add('error');
            radio.setAttribute('aria-invalid', 'true');
          });
          
          // Create error message element
          const errorElement = document.createElement('div');
          errorElement.className = 'feeding-calculator-validation-error';
          errorElement.setAttribute('data-validation-error', fieldName);
          errorElement.textContent = message;
          errorElement.setAttribute('role', 'alert');
          
          // Insert error message after the activity-radios container
          activityRadiosContainer.insertAdjacentElement('afterend', errorElement);
        } else {
          // For other fields (weight, age), use the original logic
          field.classList.add('error');
          field.setAttribute('aria-invalid', 'true');
          
          // Create error message element
          const errorElement = document.createElement('div');
          errorElement.className = 'feeding-calculator-validation-error';
          errorElement.setAttribute('data-validation-error', fieldName);
          errorElement.textContent = message;
          errorElement.setAttribute('role', 'alert');
          
          // Insert error message after the field or its container
          const fieldContainer = field.closest('.form-field') || field.parentElement;
          if (fieldContainer) {
            fieldContainer.appendChild(errorElement);
          } else {
            field.insertAdjacentElement('afterend', errorElement);
          }
        }
      }

      clearValidationErrors(slide) {
        // Remove all error classes and messages
        const errorFields = slide.querySelectorAll('.error');
        errorFields.forEach(field => {
          field.classList.remove('error');
          field.removeAttribute('aria-invalid');
        });
        
        const errorMessages = slide.querySelectorAll('[data-validation-error]');
        errorMessages.forEach(error => error.remove());
      }

      restoreFormData() {
        const form = this.querySelector('form') || this;
        const inputs = form.querySelectorAll('input, select, textarea');
        
        inputs.forEach(input => {
          const name = input.name || input.id;
          if (!name || !(name in this.formData)) return;

          const value = this.formData[name];
          
          if (input.type === 'checkbox') {
            input.checked = Boolean(value);
          } else if (input.type === 'radio') {
            input.checked = input.value === value;
          } else if (input.tagName === 'SELECT') {
            // For selects, only restore if value exists and is not empty
            // This preserves HTML defaults (selected attribute on options)
            if (value && value !== '') {
              input.value = value;
            }
          } else {
            input.value = value || '';
          }
        });
      }

      persistToLocalStorage() {
        if (this.isDesignMode) return;
        
        try {
          const state = {
            formData: this.formData,
            navigationHistory: this.navigationHistory,
            currentSlideIndex: this.currentSlideIndex,
            timestamp: Date.now()
          };
          localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (error) {
          console.warn('Failed to save calculator state to localStorage:', error);
        }
      }

      restoreFromLocalStorage() {
        if (this.isDesignMode) return;
        
        try {
          const savedState = localStorage.getItem(this.storageKey);
          if (!savedState) return;

          const state = JSON.parse(savedState);
          
          if (state.formData) {
            this.formData = state.formData;
          }
          
          if (state.navigationHistory && Array.isArray(state.navigationHistory)) {
            this.navigationHistory = state.navigationHistory;
          }
          
          if (typeof state.currentSlideIndex === 'number') {
            this.currentSlideIndex = state.currentSlideIndex;
          }
        } catch (error) {
          console.warn('Failed to restore calculator state from localStorage:', error);
        }
      }

      resetCalculator() {
        // Clear localStorage
        try {
          localStorage.removeItem(this.storageKey);
        } catch (error) {
          console.warn('Failed to clear calculator state from localStorage:', error);
        }

        // Reset state
        this.formData = {};
        this.navigationHistory = [];
        this.currentSlideIndex = 0;

        // Clear all form fields
        const form = this.querySelector('form') || this;
        const inputs = form.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
          if (input.type === 'checkbox' || input.type === 'radio') {
            input.checked = false;
          } else {
            input.value = '';
          }
        });

        // Navigate to first slide
        if (this.slides.length > 0) {
          const firstSlide = this.slides[0];
          this.addToHistory(firstSlide, 'reset');
          this.showSlide(0, true);
        }
      }

      // Navigation History Methods

      addToHistory(slide, action = 'forward') {
        if (!slide) return;

        const historyEntry = {
          slideId: slide.id,
          slideType: slide.dataset.slideType || 'unknown',
          blockId: slide.dataset.slideId || '',
          timestamp: Date.now(),
          action: action
        };

        // Don't add duplicate consecutive entries
        const lastEntry = this.navigationHistory[this.navigationHistory.length - 1];
        if (lastEntry && lastEntry.slideId === historyEntry.slideId) {
          return;
        }

        this.navigationHistory.push(historyEntry);
      }


      clearHistory() {
        this.navigationHistory = [];
      }

      // Results Calculation and Population Methods

      async fetchProduct(productIdOrHandle) {
        try {
          // If productIdOrHandle is a numeric ID, we need to get the handle first
          // Shopify's /products/{id}.js endpoint works with handles, not numeric IDs
          // So if it's numeric, we need to fetch differently or use the handle
          let productHandle = productIdOrHandle;
          
          // If it's numeric, try to use it as-is (Shopify might accept it)
          // Otherwise assume it's already a handle
          const response = await fetch(`/products/${productHandle}.js`);
          if (!response.ok) {
            throw new Error(`Failed to fetch product: ${response.statusText}`);
        }
          const product = await response.json();
          
          // Ensure handle is included
          if (!product.handle && productHandle && !/^\d+$/.test(productHandle)) {
            // If productHandle is not numeric, use it as handle
            product.handle = productHandle;
          }
          
          return product;
        } catch (error) {
          console.error('Error fetching product:', error, { productIdOrHandle });
          return null;
        }
      }

      async populateResultsSlide(slide, blockId) {
        // Determine if we have mixed products or single product
        const wetRatio = parseFloat(this.formData.wet_ratio) || 0;
        const dryRatio = parseFloat(this.formData.dry_ratio) || 100;
        const hasMixedProducts = this.formData.selected_dry_product_data && this.formData.selected_wet_product_data;
        
        // Check if it's truly mixed (not 100% dry or 100% wet)
        const isMixed = hasMixedProducts && wetRatio > 0.1 && dryRatio > 0.1;
        
        // Get product cards
        const product1Card = slide.querySelector(`#product-1-${blockId}`);
        const product2Card = slide.querySelector(`#product-2-${blockId}`);
        
        // Get heading element outside (for single products)
        const headingEl = slide.querySelector(`#results-heading-${blockId}`);
        
        // Get products grid to add class for single product styling
        const productsGrid = slide.querySelector('.feeding-calculator-slide__results-products-grid');
        
        if (isMixed) {
          // Dual products: show both cards, hide heading
          if (product1Card) product1Card.style.display = 'flex';
          if (product2Card) product2Card.style.display = 'flex';
          
          // Remove single product class
          if (productsGrid) {
            productsGrid.classList.remove('feeding-calculator-slide__results-products-grid--single');
          }
          
          // Hide heading for dual products
          if (headingEl) {
            headingEl.style.display = 'none';
          }
          
          // Populate dual products
          await this.populateProductResults(slide, blockId, true);
        } else {
          // Single product: show only product 1, hide product 2
          if (product1Card) product1Card.style.display = 'flex';
          if (product2Card) product2Card.style.display = 'none';
          
          // Add single product class for styling
          if (productsGrid) {
            productsGrid.classList.add('feeding-calculator-slide__results-products-grid--single');
          }
          
          // Show heading for single products
          if (headingEl) {
            headingEl.style.display = 'block';
          }
              
          // Determine which product to show (dry or wet)
          let productData = null;
          if (dryRatio > 0.1 && this.formData.selected_dry_product_data) {
            productData = this.formData.selected_dry_product_data;
          } else if (wetRatio > 0.1 && this.formData.selected_wet_product_data) {
            productData = this.formData.selected_wet_product_data;
          }
          
          if (productData) {
            // Populate single product (using index 1) - grams will be shown in product amount
            await this.populateProductResults(slide, blockId, false, productData);
          }
        }
        
        // Initialize quantity selectors AFTER product cards are shown and populated
        // Use double requestAnimationFrame to ensure browser has processed display change
        // and custom elements are fully initialized before accessing them
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Initialize quantity selectors - this ensures the quantity property is available
            this.initQuantitySelectors();
            // Setup quantity change listeners to update duration
            this.setupQuantityChangeListeners(slide, blockId);
          });
        });
        
        // Final validation check after all Skio blocks are rendered
        setTimeout(() => {
          this.updateAddToCartButtonStates(slide, blockId);
          // Update wrapper height after content is fully loaded
          this.updateWrapperHeight();
        }, 500);
      }

      /**
       * Unified function to populate product results for both single and dual products
       * @param {HTMLElement} slide - The results slide element
       * @param {string} blockId - The block ID
       * @param {boolean} isDual - Whether this is dual product results
       * @param {Object} singleProductData - Product data for single product (only used when isDual is false)
       */
      async populateProductResults(slide, blockId, isDual = false, singleProductData = null) {
        if (isDual) {
          const dryProductData = this.formData.selected_dry_product_data;
          const wetProductData = this.formData.selected_wet_product_data;

          if (!dryProductData || !wetProductData) {
            console.warn('Both products must be selected for dual results');
            return;
          }

          // Calculate daily grams for mixed feeding
          const result = this.calculateDailyGrams();
          const { wet, dry } = typeof result === 'object' && result.wet !== undefined ? result : { wet: 0, dry: 0 };

          // Populate Product 1 (Wet) and Product 2 (Dry) in parallel
          await Promise.all([
            this.populateProductCard(slide, blockId, 1, wetProductData, wet),
            this.populateProductCard(slide, blockId, 2, dryProductData, dry)
          ]);

          // Store calculated values
          this.formData.result_wet_daily_grams = wet;
          this.formData.result_dry_daily_grams = dry;
          this.formData.result_wet_price = wetProductData.price || 0;
          this.formData.result_dry_price = dryProductData.price || 0;
          this.persistToLocalStorage();
          
          // Update button states after Skio blocks are rendered
          setTimeout(() => {
            this.updateAddToCartButtonStates(slide, blockId);
          }, 300);
              } else {
          // Single product - always use index 1
          if (!singleProductData) return;

          await this.populateProductCard(slide, blockId, 1, singleProductData);
          
          // Update button states after Skio block is rendered
          setTimeout(() => {
            this.updateAddToCartButtonStates(slide, blockId);
          }, 300);
        }
      }

      /**
       * Unified function to populate a product card (works for both single and dual)
       * @param {HTMLElement} slide - The slide element
       * @param {string} blockId - The block ID
       * @param {number} index - Product index (1 for single, 1 or 2 for dual)
       * @param {Object} productData - Product data
       * @param {number} dailyGrams - Daily grams (calculated or passed)
       */
      async populateProductCard(slide, blockId, index, productData, dailyGrams = null) {
        // Always use index-based selectors (1 for single, 1 or 2 for dual)
        const nameSelector = `#product-${index}-name-${blockId}`;
        const imageSelector = `#product-${index}-image-${blockId}`;
        const amountSelector = `#product-${index}-amount-${blockId}`;
        const variantSelectorId = `variant-selector-${index}-${blockId}`;
        const durationSelector = `#product-${index}-duration-text-${blockId}`;
        const durationContainerSelector = `#product-${index}-duration-${blockId}`;
        const subscriptionSelector = `#subscription-container-${index}-${blockId}`;

        // Update product name
        const nameEl = slide.querySelector(nameSelector);
        if (nameEl) {
          nameEl.textContent = productData.title;
        }

        // Update product image
        const imageEl = slide.querySelector(imageSelector);
        if (imageEl) {
          const imageUrl = FoxTheme.getProductImageUrl?.(productData) || productData.image || '';
          if (imageUrl) {
            imageEl.innerHTML = `<img src="${imageUrl}" alt="${productData.title}" loading="lazy">`;
          }
        }

        // Calculate daily grams if not provided (for single product)
        if (dailyGrams === null) {
          // Temporarily set selected_product_data for calculation
          const originalProductData = this.formData.selected_product_data;
          this.formData.selected_product_data = productData;
          dailyGrams = this.calculateDailyGrams();
          this.formData.selected_product_data = originalProductData;
        }

        // Update daily grams display (show for both single and dual products)
        const amountEl = slide.querySelector(amountSelector);
        if (amountEl && dailyGrams !== null) {
          // Show amount for both single and dual products (same format)
          amountEl.innerHTML = `<span class="amount-value">${Math.round(dailyGrams)}</span> grams per day`;
          amountEl.style.display = 'block';
        } else if (amountEl) {
          // Hide amount if no grams calculated
          amountEl.style.display = 'none';
        }

        // Populate variant selector and sync duration / pricing with the selected option
        const syncedProductData =
          (await this.populateVariantSelector(slide, blockId, productData, index, dailyGrams)) || productData;

        // Render Skio block
        await this.renderSkioBlock(slide, blockId, syncedProductData, index);
      }

      getResultsProductDataKey(slide, blockId, index, productData) {
        const product2Card = slide.querySelector(`#product-2-${blockId}`);
        const isDualProduct = product2Card && product2Card.style.display !== 'none';

        if (isDualProduct) {
          if (index === 1) return 'selected_wet_product_data';
          if (index === 2) return 'selected_dry_product_data';
          return null;
        }

        if (this.formData.selected_dry_product_data?.id === productData.id) {
          return 'selected_dry_product_data';
        }
        if (this.formData.selected_wet_product_data?.id === productData.id) {
          return 'selected_wet_product_data';
        }
        return null;
      }

      syncResultsVariantFromSelect(slide, blockId, index, productData, product, dailyGrams) {
        const select = slide.querySelector(`select[data-product-index="${index}"]`);
        if (!select || !product?.variants?.length) {
          return productData;
        }

        const selectedVariant = product.variants.find(
          (variant) => String(variant.id) === String(select.value)
        );
        if (!selectedVariant) {
          return productData;
        }

        const productDataKey = this.getResultsProductDataKey(slide, blockId, index, productData);
        const targetProductData = productDataKey ? this.formData[productDataKey] : productData;
        if (!targetProductData) {
          return productData;
        }

        targetProductData.variant_id = String(selectedVariant.id);
        targetProductData.variant_title = selectedVariant.title;
        targetProductData.price = selectedVariant.price;

        const product2Card = slide.querySelector(`#product-2-${blockId}`);
        const isDualProduct = product2Card && product2Card.style.display !== 'none';
        if (!isDualProduct) {
          this.formData.selected_product_data = targetProductData;
        }

        const quantityInput = slide.querySelector(
          `#product-${index}-${blockId} quantity-selector input[name="quantity"]`
        );
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
        const daysForOneBag = selectedVariant.title
          ? this.calculateDays(dailyGrams, selectedVariant.title)
          : 0;
        this.updateDurationDisplay(slide, blockId, index, daysForOneBag * quantity);

        return targetProductData;
      }

      async populateVariantSelector(slide, blockId, productData, index = null, dailyGrams = 0) {
        // Always use index-based selector (index 1 for single, 1 or 2 for dual)
        const variantSelectorId = `variant-selector-${index}-${blockId}`;
        const variantSelectorEl = slide.querySelector(`#${variantSelectorId}`);
        
        if (!variantSelectorEl || (!productData.id && !productData.handle)) {
          console.warn(`Variant selector element not found: #${variantSelectorId}`);
          return productData;
        }

        try {
          // Fetch full product data with variants - use handle if available, otherwise ID
          const productHandle = productData.handle || productData.id;
          const product = await this.fetchProduct(productHandle);
          if (!product || !product.variants || product.variants.length === 0) {
            return productData;
          }

          // Clear existing options
          variantSelectorEl.innerHTML = '';

          // Create wrapper div with .select class
          const selectWrapper = document.createElement('div');
          selectWrapper.className = 'select';
          
          // Create select element
          const select = document.createElement('select');
          select.id = `variant-select-${index}-${blockId}`;
          select.name = `variant_id_${index}`;
          select.setAttribute('data-variant-select', 'true');
          select.setAttribute('data-product-index', index);
          select.className = 'form-control form-control--select feeding-calculator-slide__results-variant-select';
          
          // Add options for each variant
          let hasSelectedVariant = false;
          product.variants.forEach(variant => {
            const option = document.createElement('option');
            option.value = variant.id;
            option.textContent = variant.title;
            if (String(variant.id) === String(productData.variant_id)) {
              option.selected = true;
              hasSelectedVariant = true;
            }
            select.appendChild(option);
          });

          if (!hasSelectedVariant && product.variants.length > 0) {
            select.value = String(product.variants[0].id);
          }
          
          // Create caret icon SVG
          const caretIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          caretIcon.setAttribute('class', 'icon icon-caret-down icon--2xs');
          caretIcon.setAttribute('viewBox', '0 0 20 20');
          caretIcon.setAttribute('fill', 'none');
          caretIcon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', 'M16.25 7.5L10 13.75L3.75 7.5');
          path.setAttribute('stroke', 'currentColor');
          path.setAttribute('stroke-width', '1.5');
          path.setAttribute('stroke-linecap', 'round');
          path.setAttribute('stroke-linejoin', 'round');
          caretIcon.appendChild(path);
          
          // Append select and icon to wrapper
          selectWrapper.appendChild(select);
          selectWrapper.appendChild(caretIcon);
          variantSelectorEl.appendChild(selectWrapper);

          const product2Card = slide.querySelector(`#product-2-${blockId}`);
          const isDualProduct = product2Card && product2Card.style.display !== 'none';

          const syncedProductData = this.syncResultsVariantFromSelect(
            slide,
            blockId,
            index,
            productData,
            product,
            dailyGrams
          );

          // Add change listener to update Skio block and duration when variant changes
          select.addEventListener('change', async (event) => {
            const selectedVariantId = event.target.value;
            let selectedVariant = product.variants.find(v => String(v.id) === String(selectedVariantId));
            
            // Re-fetch product if variant not found and variants are missing
            if (!selectedVariant && (!product.variants || product.variants.length === 0)) {
              const productHandle = productData.handle || productData.id;
              const refreshedProduct = await this.fetchProduct(productHandle);
              if (refreshedProduct?.variants) {
                product.variants = refreshedProduct.variants;
                selectedVariant = refreshedProduct.variants.find(v => String(v.id) === String(selectedVariantId));
              }
            }
            
            if (!selectedVariant) return;

            let grams;
            if (isDualProduct) {
              const result = this.calculateDailyGrams();
              const { wet, dry } = typeof result === 'object' && result.wet !== undefined ? result : { wet: 0, dry: 0 };
              grams = index === 1 ? wet : dry;
            } else {
              grams = this.calculateDailyGrams();
            }

            const targetProductData = this.syncResultsVariantFromSelect(
              slide,
              blockId,
              index,
              productData,
              product,
              grams
            );
            if (!targetProductData) return;
            
            // Update Skio block first, then dispatch variantChanged event
            await this.renderSkioBlock(slide, blockId, targetProductData, index);
            
            // Dispatch variantChanged event after a short delay to ensure Skio component is connected
            setTimeout(() => {
              const variantChangedEvent = new CustomEvent('variantChanged', {
                bubbles: true,
                detail: { variantId: selectedVariantId }
              });
              document.dispatchEvent(variantChangedEvent);
            }, 150);
            
            this.persistToLocalStorage();
          });

          return syncedProductData;
        } catch (error) {
          console.error('Error populating variant selector:', error);
          return productData;
        }
      }

      /**
       * Unified function to render Skio block for both single and dual products
       * Uses section rendering API to fetch Liquid-rendered Skio block
       * @param {HTMLElement} slide - The slide element
       * @param {string} blockId - The block ID
       * @param {Object} productData - Product data
       * @param {number} index - Product index (1 for single, 1 or 2 for dual)
       */
      async renderSkioBlock(slide, blockId, productData, index = null) {
        if (!productData || (!productData.id && !productData.handle)) return;

        // Always use index-based selector
        const subscriptionSelector = `#subscription-container-${index}-${blockId}`;
        const subscriptionContainer = slide.querySelector(subscriptionSelector);
        if (!subscriptionContainer) return;

        try {
          // Get product handle - use handle if available, otherwise try to get from product data
          const productHandle = productData.handle || productData.id;
          if (!productHandle) return;

          // Build section rendering URL
          const params = new URLSearchParams({
            section_id: 'skio-plan-picker-dynamic',
            product_handle: productHandle,
            product_form_id: `product-form-${blockId}-${productData.id || productHandle}`
          });

          // Add variant ID if available
          if (productData.variant_id) {
            params.append('variant_id', productData.variant_id);
          }

          // Fetch the section via product page (section rendering API)
          const sectionUrl = `/products/${productHandle}?${params.toString()}`;
          const response = await fetch(sectionUrl);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch Skio block: ${response.status}`);
          }

          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Find the section content - Shopify wraps sections in a div with id starting with 'shopify-section-'
          const sectionElement = doc.querySelector('#shopify-section-skio-plan-picker-dynamic') || 
                                doc.querySelector('.shopify-section') || 
                                doc.body;
          
          // Clear existing content and inject the rendered Skio block
          subscriptionContainer.innerHTML = '';
          
          // Clone and append all child nodes (to avoid script execution issues)
          if (sectionElement) {
            const fragment = document.createDocumentFragment();
            Array.from(sectionElement.childNodes).forEach(node => {
              fragment.appendChild(node.cloneNode(true));
            });
            subscriptionContainer.appendChild(fragment);
          }
          
          // Wait for Skio component to initialize, then set up validation
          setTimeout(() => {
            this.setupSkioValidation(slide, blockId, index);
            // Update wrapper height after Skio block loads (content may have changed)
            this.updateWrapperHeight();
          }, 200);

        } catch (error) {
          console.error('Error rendering Skio block:', error);
        }
      }

      /**
       * Check if subscription is selected but no frequency plan is chosen
       * @param {HTMLElement} slide - The results slide
       * @param {string} blockId - The block ID
       * @param {number} productIndex - Product index (1 or 2)
       * @returns {boolean} - True if subscription selected but no frequency chosen
       */
      isSubscriptionSelectedWithoutFrequency(slide, blockId, productIndex) {
        const subscriptionContainer = slide.querySelector(`#subscription-container-${productIndex}-${blockId}`);
        if (!subscriptionContainer) return false;
        
        const skioPicker = subscriptionContainer.querySelector('skio-plan-picker');
        if (!skioPicker?.shadowRoot) return false;
        
        // Check if one-time purchase is selected (no subscription)
        const oneTimeInput = skioPicker.shadowRoot.querySelector('.group-input[value="One time"]:checked');
        if (oneTimeInput) return false; // One-time purchase selected, no need to check frequency
        
        // Check if a subscription group is selected (not one-time purchase)
        const shadowSelectedGroup = skioPicker.shadowRoot.querySelector('.group-container--selected');
        if (!shadowSelectedGroup) return false;
        
        // Skip if this is the one-time purchase group (it won't have a dropdown)
        const oneTimeGroup = shadowSelectedGroup.querySelector('.group-input[value="One time"]');
        if (oneTimeGroup) return false; // One-time purchase group selected
        
        // Check if frequency dropdown has a valid selection (not placeholder)
        const shadowDropdown = shadowSelectedGroup.querySelector('.selling-plan-dropdown');
        if (!shadowDropdown || shadowDropdown.selectedIndex < 0) return true; // No dropdown or no selection
        
        const selectedOption = shadowDropdown.options[shadowDropdown.selectedIndex];
        if (!selectedOption || !selectedOption.value) return true; // No value selected
        
        // Check if it's a placeholder option
        const optionText = selectedOption.textContent.trim().toLowerCase();
        if (optionText.includes('please select') || optionText.includes('select frequency')) {
          return true; // Placeholder selected, frequency not chosen
        }
        
        return false; // Valid frequency selected
      }

      /**
       * Update add to cart button states based on subscription selection
       * @param {HTMLElement} slide - The results slide
       * @param {string} blockId - The block ID
       */
      updateAddToCartButtonStates(slide, blockId) {
        // Check both products (1 and 2)
        for (let productIndex = 1; productIndex <= 2; productIndex++) {
          const button = slide.querySelector(`[data-add-to-cart="true"][data-product-index="${productIndex}"]`);
          if (!button) continue;
          
          const productCard = slide.querySelector(`#product-${productIndex}-${blockId}`);
          if (!productCard || productCard.style.display === 'none') {
            // Product card is hidden, ensure button is enabled (will be hidden anyway)
            button.disabled = false;
            continue;
          }
          
          // Check if subscription is selected but no frequency chosen
          const needsFrequency = this.isSubscriptionSelectedWithoutFrequency(slide, blockId, productIndex);
          button.disabled = needsFrequency;
          button.setAttribute('aria-disabled', needsFrequency ? 'true' : 'false');
        }
      }

      /**
       * Set up Skio validation listeners for a specific product
       * @param {HTMLElement} slide - The results slide
       * @param {string} blockId - The block ID
       * @param {number} productIndex - Product index (1 or 2)
       */
      setupSkioValidation(slide, blockId, productIndex) {
        const subscriptionContainer = slide.querySelector(`#subscription-container-${productIndex}-${blockId}`);
        if (!subscriptionContainer) return;
        
        const skioPicker = subscriptionContainer.querySelector('skio-plan-picker');
        if (!skioPicker) return;
        
        // Initial check
        this.updateAddToCartButtonStates(slide, blockId);
        
        // Listen for Skio change events
        const handleSkioChange = () => {
          this.updateAddToCartButtonStates(slide, blockId);
        };
        
        // Listen for Skio's custom events
        skioPicker.addEventListener('skio::update-selling-plan', handleSkioChange);
        skioPicker.addEventListener('change', handleSkioChange);
        
        // Also listen for changes in shadow DOM dropdown
        if (skioPicker.shadowRoot) {
          const shadowDropdown = skioPicker.shadowRoot.querySelector('.selling-plan-dropdown');
          if (shadowDropdown) {
            shadowDropdown.addEventListener('change', handleSkioChange);
          }
          
          // Listen for group selection changes (radio buttons)
          const groupInputs = skioPicker.shadowRoot.querySelectorAll('.group-input');
          groupInputs.forEach(input => {
            input.addEventListener('change', handleSkioChange);
          });
        }
      }

      updateDurationDisplay(slide, blockId, index, days) {
        const selector = `#product-${index}-duration-text-${blockId}`;
        const durationTextEl = document.querySelector(selector) || slide.querySelector(selector);
        const containerSelector = `#product-${index}-duration-${blockId}`;
        const durationContainerEl = document.querySelector(containerSelector) || slide.querySelector(containerSelector);
        
        if (durationTextEl) {
          if (days > 0 && !isNaN(days)) {
            durationTextEl.innerHTML = `Should last <span class="duration-value">${days}</span> ${days === 1 ? 'day' : 'days'}`;
            if (durationContainerEl) {
              durationContainerEl.style.display = 'flex';
            }
          } else {
            durationTextEl.textContent = '';
            if (durationContainerEl) {
              durationContainerEl.style.display = 'none';
            }
          }
        }
      }

      setupQuantityChangeListeners(slide, blockId) {
        // Find all quantity inputs in the results slide
        const quantityInputs = slide.querySelectorAll('quantity-selector input[type="number"][name="quantity"]');
        
        quantityInputs.forEach((input, idx) => {
          // Determine product index from input's container or data attributes
          // Try to find the product card that contains this input
          const productCard = input.closest(`#product-1-${blockId}, #product-2-${blockId}`);
          let productIndex = null;
          
          if (productCard) {
            // Extract index from product card ID (e.g., "product-1-{blockId}" -> 1)
            const cardIdMatch = productCard.id.match(/product-(\d+)-/);
            if (cardIdMatch) {
              productIndex = parseInt(cardIdMatch[1], 10);
            }
          }
          
          // Fallback: try to get from data-index or use idx + 1
          if (!productIndex) {
            productIndex = parseInt(input.dataset.index || (idx + 1), 10);
          }
          
          // Add change event listener
          input.addEventListener('change', () => {
            this.updateDurationForQuantity(slide, blockId, productIndex);
          });
          
          // Also listen for input event (for manual typing)
          input.addEventListener('input', () => {
            this.updateDurationForQuantity(slide, blockId, productIndex);
          });
        });
      }

      updateDurationForQuantity(slide, blockId, productIndex) {
        // Get quantity value
        const quantityInput = slide.querySelector(`#product-${productIndex}-${blockId} quantity-selector input[name="quantity"]`);
        if (!quantityInput) return;
        
        const quantity = parseInt(quantityInput.value, 10) || 1;
        
        // Get product data based on index
        let productData = null;
        let dailyGrams = null;
        
        // Check if this is dual product scenario
        const product2Card = slide.querySelector(`#product-2-${blockId}`);
        const isDualProduct = product2Card && product2Card.style.display !== 'none';
        
        if (isDualProduct) {
          // Dual products - get grams from formData
          if (productIndex === 1) {
            productData = this.formData.selected_wet_product_data;
            dailyGrams = this.formData.result_wet_daily_grams;
          } else if (productIndex === 2) {
            productData = this.formData.selected_dry_product_data;
            dailyGrams = this.formData.result_dry_daily_grams;
          }
        } else {
          // Single product
          productData = this.formData.selected_product_data || 
                       this.formData.selected_dry_product_data || 
                       this.formData.selected_wet_product_data;
          
          // Recalculate daily grams for single product
          if (productData) {
            const originalProductData = this.formData.selected_product_data;
            this.formData.selected_product_data = productData;
            dailyGrams = this.calculateDailyGrams();
            this.formData.selected_product_data = originalProductData;
          }
        }
        
        if (!productData || !dailyGrams) return;
        
        // Get variant title from variant selector
        const variantSelect = slide.querySelector(`select[data-product-index="${productIndex}"]`);
        let variantTitle = productData.variant_title || '';
        
        if (variantSelect && variantSelect.value) {
          const selectedOption = variantSelect.options[variantSelect.selectedIndex];
          if (selectedOption) {
            variantTitle = selectedOption.textContent || variantTitle;
          }
        }
        
        if (!variantTitle) return;
        
        // Calculate days with quantity multiplier
        // The calculateDays function calculates for one bag, so we multiply bag weight by quantity
        const daysForOneBag = this.calculateDays(dailyGrams, variantTitle);
        const totalDays = daysForOneBag * quantity;
        
        // Update duration display
        this.updateDurationDisplay(slide, blockId, productIndex, totalDays);
      }

      /**
       * Unified function to calculate daily grams for both single and mixed products
       * @returns {number|{wet: number, dry: number}} - Single number for single product, object with wet/dry for mixed products
       */
      calculateDailyGrams() {
        const petType = this.formData.pet_type;
        const dryProductData = this.formData.selected_dry_product_data;
        const wetProductData = this.formData.selected_wet_product_data;
        const singleProductData = this.formData.selected_product_data;
        
        // Check if we have mixed products (both dry and wet selected)
        const wetRatio = parseFloat(this.formData.wet_ratio) || 0;
        const dryRatio = parseFloat(this.formData.dry_ratio) || 100;
        const isMixed = dryProductData && wetProductData && wetRatio > 0.1 && dryRatio > 0.1;
        
        if (isMixed) {
          return this.calculateMixedDailyGrams();
        }
        
        // Single product calculation
        const productData = singleProductData;
        if (!petType || !productData) return 0;

        const weight = petType === 'cat' ? parseFloat(this.formData.cat_weight) : parseFloat(this.formData.dog_weight);
        const age = petType === 'cat' ? this.formData.cat_age : this.formData.dog_age;
        const ageText = petType === 'cat' ? (this.formData.cat_age_text || '') : (this.formData.dog_age_text || '');
        let activity = petType === 'cat' ? this.formData.cat_activity : this.formData.dog_activity;
        const treats = petType === 'cat' ? this.formData.cat_treats : this.formData.dog_treats;

        if (!weight || isNaN(weight)) return 0;

        // Get calorie density from product metafields
        const calDensity = productData.calories || 0;
        if (!calDensity || calDensity <= 0) return 0;

        // Get category from product metafield - CRITICAL: must use product category, not pet type
        // Use module to determine if it should be puppy/kitten based on age
        const defaultCategory = productData.category || petType;
        const category = this.calculator 
          ? this.calculator.determineCategory(age, ageText, petType, defaultCategory)
          : defaultCategory;
        
        // Use calculator module for core calculation
        if (!this.calculator) {
          console.warn('Calculator module not initialized');
          return 0;
        }

        // Get activity override parameters from DOM if available (for kitten/puppy override logic)
        const calculatorElement = this.closest('.feeding-calculator-main__slides') || this;
        let ageLimit = null;
        let kittenPuppyActivity = null;
        
        if (category === 'cat' || petType === 'cat') {
          ageLimit = calculatorElement.getAttribute('data-kitten-age');
          kittenPuppyActivity = calculatorElement.getAttribute('data-kitten-puppy-activity');
        } else if (category === 'dog' || petType === 'dog') {
          ageLimit = calculatorElement.getAttribute('data-puppy-age');
          kittenPuppyActivity = calculatorElement.getAttribute('data-kitten-puppy-activity');
        }

        const params = {
          weight,
          petType,
          age,
          ageText,
          activity, // Pass raw activity - module will parse and apply overrides
          treats,
          calDensity,
          category, // Already determined by module (puppy/kitten if applicable)
          ageLimit: ageLimit !== null ? parseFloat(ageLimit) : undefined,
          kittenPuppyActivity: kittenPuppyActivity !== null ? parseFloat(kittenPuppyActivity) : undefined
        };
        
        return this.calculator.calculateDailyGrams(params);
      }

      // Delegate to calculator module
      getRERMultiplier(category) {
        return this.calculator ? this.calculator.getRERMultiplier(category) : 0.75;
      }

      parseAgeValue(age, petType) {
        return this.calculator ? this.calculator.parseAgeValue(age, petType) : 1.0;
      }

      calculateMixedDailyGrams() {
        const petType = this.formData.pet_type;
        const dryData = this.formData.selected_product_data;
        const dryProductData = this.formData.selected_dry_product_data;
        const wetProductData = this.formData.selected_wet_product_data;

        if (!petType || !dryProductData || !wetProductData) {
          return { wet: 0, dry: 0 };
        }

        const weight = petType === 'cat' ? parseFloat(this.formData.cat_weight) : parseFloat(this.formData.dog_weight);
        const age = petType === 'cat' ? this.formData.cat_age : this.formData.dog_age;
        const ageText = petType === 'cat' ? (this.formData.cat_age_text || '') : (this.formData.dog_age_text || '');
        const activity = petType === 'cat' ? this.formData.cat_activity : this.formData.dog_activity;
        const treats = petType === 'cat' ? this.formData.cat_treats : this.formData.dog_treats;
        const wetRatio = parseFloat(this.formData.wet_ratio) || 50;
        const dryRatio = parseFloat(this.formData.dry_ratio) || 50;

        if (!weight || isNaN(weight)) return { wet: 0, dry: 0 };

        // Get calorie densities
        const dryDensity = parseFloat(dryProductData.calories) || 0;
        const wetDensity = parseFloat(wetProductData.calories) || 0;

        if (!dryDensity || !wetDensity) return { wet: 0, dry: 0 };

        // Use calculator module for core calculation
        // Determine category (puppy/kitten) based on age if needed
        if (!this.calculator) {
          console.warn('Calculator module not initialized');
          return { wet: 0, dry: 0 };
        }

        // Determine category from product metafield - use dry product category as default
        // Use module to determine if it should be puppy/kitten based on age
        const defaultCategory = dryProductData.category || petType;
        const category = this.calculator 
          ? this.calculator.determineCategory(age, ageText, petType, defaultCategory)
          : defaultCategory;

        // Get activity override parameters from DOM if available (for kitten/puppy override logic)
        const calculatorElement = this.closest('.feeding-calculator-main__slides') || this;
        let ageLimit = null;
        let kittenPuppyActivity = null;
        
        if (category === 'cat' || petType === 'cat') {
          ageLimit = calculatorElement.getAttribute('data-kitten-age');
          kittenPuppyActivity = calculatorElement.getAttribute('data-kitten-puppy-activity');
        } else if (category === 'dog' || petType === 'dog') {
          ageLimit = calculatorElement.getAttribute('data-puppy-age');
          kittenPuppyActivity = calculatorElement.getAttribute('data-kitten-puppy-activity');
        }

        const params = {
          weight,
          petType,
          age,
          ageText,
          activity, // Pass raw activity - module will parse and apply overrides
          treats,
          wetRatio,
          dryRatio,
          wetCalDensity: wetDensity,
          dryCalDensity: dryDensity,
          category, // Already determined by module (puppy/kitten if applicable)
          ageLimit: ageLimit !== null ? parseFloat(ageLimit) : undefined,
          kittenPuppyActivity: kittenPuppyActivity !== null ? parseFloat(kittenPuppyActivity) : undefined
        };
        
        const result = this.calculator.calculateMixedDailyGrams(params);
        
        // Apply treats adjustment (reduce by 10% when treats are selected)
        // This matches the single product calculation behavior
        // Check petType directly since category can be 'puppy' or 'kitten' for young pets
        if (treats && (petType === 'cat' || petType === 'dog')) {
          if (typeof result === 'object' && result.wet !== undefined && result.dry !== undefined) {
            return {
              wet: Math.round(result.wet * 0.9),
              dry: Math.round(result.dry * 0.9)
            };
          }
        }
        
        return result;
      }


      calculateDays(dailyGrams, variantTitle) {
        if (!dailyGrams || dailyGrams <= 0 || !variantTitle) {
          return 0;
        }

        // Parse variant title to get bag weight (matching old calculator logic)
        const variantTitleLower = variantTitle.toLowerCase();
        let bagWeight = 0;
        let days = 0;

        if (variantTitleLower.includes('kg')) {
          // Extract number before "kg", multiply by 1000 to get grams
          const kgMatch = variantTitleLower.match(/(\d+\.?\d*)\s*kg/);
          if (kgMatch) {
            bagWeight = Number(kgMatch[1]) * 1000;
            days = bagWeight / dailyGrams;
          }
        } else if (variantTitleLower.includes('g') && variantTitleLower.includes('x')) {
          // Handle format like "3x400g" or "3 x 400g"
          const parts = variantTitleLower.split(' - ')[0]; // Get part before " - " if exists
          const xMatch = parts.match(/(\d+)\s*x\s*(\d+)\s*g/i);
          if (xMatch) {
            const amtBags = Number(xMatch[1]);
            const amtPerBag = Number(xMatch[2]);
            bagWeight = amtBags * amtPerBag;
            days = bagWeight / dailyGrams;
          }
        } else if (variantTitleLower.includes('g') && !variantTitleLower.includes('x')) {
          // Handle format like "400g"
          const gMatch = variantTitleLower.match(/(\d+\.?\d*)\s*g/);
          if (gMatch) {
            bagWeight = Number(gMatch[1]) * 1000;
            days = (bagWeight / dailyGrams) / 1000;
          }
        }

        return Math.round(days);
      }

      formatMoney(cents) {
        // Use theme's currency formatter which respects locale and currency settings
        if (typeof FoxTheme !== 'undefined' && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
          const moneyFormat = (FoxTheme.settings && FoxTheme.settings.moneyFormat) || '{{amount}}';
          return FoxTheme.Currency.formatMoney(cents, moneyFormat);
        }
        
        // Fallback to Shopify.formatMoney if available
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents);
        }
        
        // Final fallback: Use Intl.NumberFormat with currency from window.Shopify if available
        const amount = cents / 100;
        const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
        const locale = (window.Shopify && window.Shopify.locale) || 'en-GB';
        
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2
        }).format(amount);
      }

      handleAddToCart(event) {
        const button = event.target.closest('[data-add-to-cart="true"]');
        if (!button) return;

        // Check if button is within this calculator component
        const calculator = button.closest('feeding-calculator');
        if (calculator !== this) return;

        event.preventDefault();
        event.stopPropagation();

        // Get the current slide to find product data
        const currentSlide = this.slides[this.currentSlideIndex];
        if (!currentSlide) return;

        // Handle add to cart (individual product button)
        this.addProductToCart(button, currentSlide);
      }

      /**
       * Unified function to add products to cart - handles both single and dual products
       * @param {HTMLElement} button - The add to cart button
       * @param {HTMLElement} slide - The current slide element
       */
      async addProductToCart(button, slide) {
        // Get product index from button data attribute
        const productIndex = button.dataset.productIndex;
        if (!productIndex) {
          console.warn('Product index not found on button');
          return;
        }

        // Get product data based on index
        let productData = null;
        if (productIndex === '1') {
          // Product 1 could be wet (in dual) or single product
          productData = this.formData.selected_wet_product_data || this.formData.selected_product_data;
        } else if (productIndex === '2') {
          // Product 2 is always dry (in dual)
          productData = this.formData.selected_dry_product_data;
        }

        if (!productData || !productData.variant_id) {
          console.warn('Product data not found or variant ID missing');
          return;
        }

        // Show loading state
        button.classList.add('btn--loading');
        button.setAttribute('aria-disabled', 'true');

        try {
          // Prepare config for FormData (remove Content-Type header)
          const config = FoxTheme.utils.fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const blockId = slide.dataset.slideId || slide.id.replace('slide-', '');

          // Get selected variant ID from variant selector if available
          let variantId = productData.variant_id;
          const variantSelect = slide.querySelector(`select[data-product-index="${productIndex}"]`);
          if (variantSelect) {
            variantId = variantSelect.value;
          }

          // Get selling plan for this product
          const productFormId = `product-form-${blockId}-${productData.id}`;
          let sellingPlanInput = slide.querySelector(`#subscription-container-${productIndex}-${blockId} input[name="selling_plan"]`);
          if (!sellingPlanInput) {
            const productForm = document.getElementById(productFormId);
            if (productForm) {
              sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
            }
          }
          if (!sellingPlanInput) {
            sellingPlanInput = slide.querySelector(`[data-product-id="${productData.id}"] input[name="selling_plan"]`);
          }

          const sellingPlanId = sellingPlanInput && sellingPlanInput.value ? sellingPlanInput.value : null;

          // Get quantity from quantity selector if available
          let quantity = 1;
          const quantityInput = slide.querySelector(`#quantity-container-${productIndex}-${blockId} input[name="quantity"]`);
          if (quantityInput) {
            const qtyValue = parseInt(quantityInput.value, 10);
            if (!isNaN(qtyValue) && qtyValue > 0) {
              quantity = qtyValue;
            }
          }

          // Prepare form data
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', quantity);
          
          // Add selling plan if a subscription is selected
          if (sellingPlanId) {
            formData.append('selling_plan', sellingPlanId);
          }

          // Collect sections for cart drawer updates
          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
          );
          formData.append('sections', sectionsToBundle);
          formData.append('sections_url', window.location.pathname);

          // Add to cart
          const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
            ...config,
            body: formData
          });

          // Check if response is ok before parsing JSON
          if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
              const errorText = await response.text();
              if (errorText) {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.description || errorJson.message || errorText;
              }
            } catch (e) {
              errorMessage = response.statusText || `HTTP ${response.status}`;
            }
            throw new Error(errorMessage);
          }

          const parsedState = await response.json();

          if (parsedState.status) {
            // Handle error
            this.showCartError(parsedState.description || parsedState.message);
            return;
          }

          // Update cart state with latest response
          const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
          cartJson['sections'] = parsedState['sections'];

          // Publish cart update event
          if (FoxTheme.pubsub && FoxTheme.pubsub.publish) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          }

          // Dispatch product added event
          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: {
                product: parsedState,
              },
            })
          );

          // Show cart drawer if available
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.show) {
            cartDrawer.show(button);
          }

        } catch (error) {
          console.error('Error adding product to cart:', error);
          this.showCartError('Failed to add product to cart. Please try again.');
        } finally {
          // Remove loading state
          button.classList.remove('btn--loading');
          button.removeAttribute('aria-disabled');
        }
      }

      showCartError(message) {
        // Try to find error message element in the slide
        const currentSlide = this.slides[this.currentSlideIndex];
        if (currentSlide) {
          let errorEl = currentSlide.querySelector('.feeding-calculator-slide__error-message');
          if (!errorEl) {
            // Create error message element if it doesn't exist
            errorEl = document.createElement('div');
            errorEl.className = 'feeding-calculator-slide__error-message alert alert--error';
            errorEl.setAttribute('role', 'alert');
            const actionsEl = currentSlide.querySelector('.feeding-calculator-slide__results-actions');
            if (actionsEl) {
              actionsEl.insertBefore(errorEl, actionsEl.firstChild);
            } else {
              currentSlide.appendChild(errorEl);
            }
          }
          errorEl.textContent = message;
          errorEl.hidden = false;

          // Hide error after 5 seconds
          setTimeout(() => {
            errorEl.hidden = true;
          }, 5000);
        } else {
          // Fallback to alert if no slide found
          alert(message);
        }
      }

    }
  );
}

